import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import ReactDOM from 'react-dom';
 import axios from 'axios';
 import GetCookie from '../../utils/GetCookie';
 import { postNdjson } from '../../utils/postNdjson';
 import FabricSelector from '../../utils/FabricSelector';
 import DocumentSelector from './DocumentSelector';
 import ConfigureVM from './ConfigureVM';
 import LanguageSelector from './LanguageSelector';
 import AgentTranslationModal from './AgentTranslationModal';
 import ConfirmationModal from '../../utils/ConfirmationModal';
 import GenbiTemplateLibraryList from './GenbiTemplateLibraryList';
import ImportFromDocumentModal from './ImportFromDocumentModal';
import GenbiHeadingMappingModal from './GenbiHeadingMappingModal';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import AgentConfigure from './AgentConfigure';
import { agentRefId, flagReferenceCycles } from './ReferenceAgentSelector';
import ReportFormattingModal, { ColorPickerButton, defaultFormat as DEFAULT_TEXT_FORMAT, withTocDefaults } from "./ReportFormattingModal";
import GenbiResponseComponent from './GenbiResponseComponent';
import TableStructureSelector from './TableStructureSelector';
import { usePermissions } from '../../utils/PermissionsContext';
import { PERMISSIONS, capabilityChecker } from '../../utils/permissionRoutes';
/**
 * Parses a raw XML-tag response string (from doc-generate flow) into the
 * shape that GenbiResponseComponent expects: { markdown, tables, images, ... }
 */
const parseRawResponse = (raw) => {
  if (!raw || typeof raw !== 'string') return null;

  const extract = (tag) => {
    const m = raw.match(new RegExp(`<${tag}>(.*?)</${tag}>`, 's'));
    return m ? m[1].trim() : '';
  };

  const markdown = extract('MARKDOWN');

  // Parse HTMLTABLES → tables array
  const tablesRaw = extract('HTMLTABLES');
  const tables = [];
  const tableRe = /<HTMLTABLE(\d+)(?:\s+SUBTITLE\s*=\s*"([^"]*)")?\s*>(.*?)<\/HTMLTABLE\1>/gs;
  let tm;
  while ((tm = tableRe.exec(tablesRaw)) !== null) {
    tables.push({
      table_id: `preview_table_${tm[1]}`,
      tableNumber: parseInt(tm[1]),
      subtitle: tm[2] || '',
      html: tm[3].trim(),
    });
  }

  // Parse IMAGES → images array
  const imagesRaw = extract('IMAGES');
  const images = [];
  const imageRe = /<IMAGE(\d+)(?:\s+SUBTITLE\s*=\s*"([^"]*)")?(?:\s+FOOTER\s*=\s*"([^"]*)")?\s*>(.*?)<\/IMAGE\1>/gs;
  let im;
  while ((im = imageRe.exec(imagesRaw)) !== null) {
    images.push({
      img_id: `preview_img_${im[1]}`,
      figureNumber: parseInt(im[1]),
      subtitle: im[2] || '',
      footer: im[3] || '',
      s3_path: null, // not resolved yet
    });
  }

  // Parse ABBREVIATIONS
  const abbrRaw = extract('ABBREVIATIONS');
  const abbreviations = [];
  const abbrRe = /<ABBR\d+\s+SHORT\s*=\s*"([^"]*)"\s+FULL\s*=\s*"([^"]*)"\s*\/>/g;
  let ab;
  while ((ab = abbrRe.exec(abbrRaw)) !== null) {
    abbreviations.push({ short: ab[1], full: ab[2] });
  }

  if (!markdown && tables.length === 0) return null;

  return { markdown, tables, images, graphs: [], abbreviations, references: null };
};
const DEFAULT_REPORT_CONFIG = {
  paragraph: [
    {
      id: "default",
      name: "Paragraph (Default)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify"
    },
    {
      id: "paragraph-1",
      name: "Paragraph (Style 1)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify",
      backgroundColor: "#D9D9D9",
      borders: "none",
      level: 1
    },
    {
      id: "paragraph-2",
      name: "Paragraph (Style 2)",
      fontFamily: "Arial",
      fontSize: 12,
      bold: false,
      italic: false,
      color: "#000000",
      alignment: "justify",
      backgroundColor: "#F2F2F2",
      borders: "none",
      level: 2
    }
  ],
  caption: {
    fontFamily: "Arial",
    fontSize: 12,
    bold: false,
    italic: false,
    color: "#000000"
  },
  headers: [
    {
      id: "default",
      name: "Header (Default)",
      fontFamily: "Arial",
      fontSize: 14,
      bold: true,
      italic: false,
      color: "#000000",
      level: 0
    },
    {
      id: "header-level-1",
      name: "Header (Level 1)",
      fontFamily: "Arial",
      fontSize: 16,
      bold: true,
      italic: false,
      color: "#000000",
      level: 1
    },

    {
      id: "header-level-2",
      name: "Header (Level 2)",
      fontFamily: "Arial",
      fontSize: 14,
      bold: true,
      italic: true,
      color: "#000000",
      level: 2
    }
  ],
  tables: {
    general: {
      text: {
        fontFamily: "Arial",
        fontSize: 11,
        bold: false,
        italic: false,
        color: "#000000"
      },
      header: {
        fontFamily: "Arial",
        fontSize: 11,
        bold: true,
        italic: false,
        color: "#000000",
        backgroundColor: null
      },
      design: "design-1",
      captionPlacement: "above"
    },
    toc: {
      text: {
        ...DEFAULT_TEXT_FORMAT,
        fontFamily: "Arial",
        fontSize: 12,
      },
      styles: [
        { id: "toc-default", name: "TOC (Default)", level: 0, ...DEFAULT_TEXT_FORMAT },
      ],
      lotStyle: { ...DEFAULT_TEXT_FORMAT },
      lofStyle: { ...DEFAULT_TEXT_FORMAT },
      design: "design-1",
      tocLevelNum: 1,
      tocNumberSpacing: 0,
      tocShowDots: true
    }
  },
  numberingFormat: "B",
  headingNumberPeriod: false,
  numberingStartValue: 1,
  citationFormat: {
    style: "nice",
    inline: {
      fontSize: 10,
      fontColor: "#000000",
      "superscript": true
    }
  },
  figureCaptionPlacement: "above",
  lineSpacing: 1.5,
  spaceAfter: 12,
};
 


const COMPACT_MAX_SECTION_LEVEL = 1;
 const GenbiTemplateForm = forwardRef(({
   editingTemplate,
   initialFabricId,
   onSuccess,
   onError,

   onCancel,
   onDirtyChange,
   onProcessingStart,
   onProcessingEnd,
   onTemplateCreated,
 }, ref) => {
   const { can } = usePermissions();
   const [formData, setFormData] = useState({
   template_name: '',
   fabric_id: null,
   fabric_name: '',
   sectionsDict: {},
   sectionOrder: [],
   vm_config: false,  
   lang_id:1,
   format_json: DEFAULT_REPORT_CONFIG,
   global_prompt: '',
   frontpage: null
 });
 
   const [isLoading, setIsLoading] = useState(false);
   const [fabricTreeData, setFabricTreeData] = useState([]);
   const [documentSelectorOpen, setDocumentSelectorOpen] = useState(false);
   const [currentAgentIndex, setCurrentAgentIndex] = useState({ sectionKey: null, agentKey: null });
   const [isInitialFabricLoad, setIsInitialFabricLoad] = useState(false);
 
   const [vmConfiguration, setVmConfiguration] = useState(null);
   const [useValueMessage, setUseValueMessage] = useState(false);
   const [valueMessageModalOpen, setValueMessageModalOpen] = useState(false);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const [langsData, setLangsData] = useState({});
  const [isLoadingLangs, setIsLoadingLangs] = useState(false);
  // selectedLanguage: { id: number, language_name: string } — display only
  const [selectedLanguage, setSelectedLanguage] = useState({ id: 1, language_name: 'English' });

  const [isTemplateLibraryOpen, setIsTemplateLibraryOpen] = useState(false);
  const [importedLibraryTemplateId, setImportedLibraryTemplateId] = useState(null);
  const [importedLibraryTemplateName, setImportedLibraryTemplateName] = useState('');
  const [isImportFromDocOpen, setIsImportFromDocOpen] = useState(false);

  const [headingMappingId, setHeadingMappingId] = useState(null);
  const [savedMapping, setSavedMapping] = useState(null);
  const [isLoadingSavedMapping, setIsLoadingSavedMapping] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [templateToImport, setTemplateToImport] = useState(null);

  const [agentConfigureOpen, setAgentConfigureOpen] = useState(false);
  const [showReportFormatting, setShowReportFormatting] = useState(false);
  const [capabilities, setCapabilities] = useState({});
  const canHere = capabilityChecker(capabilities);
  const canWriteTemplate = editingTemplate
    ? canHere(PERMISSIONS.TEMPLATE_UPDATE)
    : can(PERMISSIONS.TEMPLATE_CREATE);
  // Sections and agents are saved as part of the template, so they follow it.
  const hasSectionManagementAccess = canWriteTemplate;
  // Section / Super Section / Sub-section / Special and Report Formatting come from the
  // Developer permission set. Template capabilities cover an open template; the global
  // profile flag covers a template still being created.
  const canSectionManagement = canHere(PERMISSIONS.SECTION_MANAGEMENT) || can(PERMISSIONS.SECTION_MANAGEMENT);
  const canReportFormatting = canHere(PERMISSIONS.REPORT_FORMATTING) || can(PERMISSIONS.REPORT_FORMATTING);
  const canUseResubmission = Boolean(
    formData.fabric_id &&
    fabricTreeData[0] &&
    String(fabricTreeData[0].id) === String(formData.fabric_id) &&
    fabricTreeData[0].resubmission_enabled
  );
  useEffect(() => {
    if (!canUseResubmission) setIsImportFromDocOpen(false);
  }, [canUseResubmission]);
  const [summarySectionsExpanded, setSummarySectionsExpanded] = useState(false);
  const [blockingOverlayCount, setBlockingOverlayCount] = useState(0);
  const [agentToConfigure, setAgentToConfigure] = useState({ sectionKey: null, agentKey: null });
  const [activePromptTab, setActivePromptTab] = useState({});
  const [showResponsePanel, setShowResponsePanel] = useState({});
  // True only when sections were loaded via "Import from Document" generate flow
  const [isDocGeneratedImport, setIsDocGeneratedImport] = useState(false);
  // const [sourceDocId, setSourceDocId] = useState(null);
  const [openParagraphDropdown, setOpenParagraphDropdown] = useState({});
  const dropdownRef = useRef(null);

  const navigate = useNavigate();

  const [showSpecialSectionInfo, setShowSpecialSectionInfo] = useState(false);
  // refer_agents notes from importing a preset (unresolved / name mismatch / circular)
  const [importReferWarnings, setImportReferWarnings] = useState([]);
  const [tableStructures, setTableStructures] = useState([]);
  const specialInfoShownRef = useRef(false);
  const globalPromptUserEditedRef = useRef(false);
  const [openSpecialSectionConfig, setOpenSpecialSectionConfig] = useState(null);
  const specialSectionConfigRef = useRef(null);
  
  const markDirty = () => { if (onDirtyChange) onDirtyChange(true); };
  const setFormDataDirty = (updater) => { setFormData(updater); markDirty(); };

  // Snapshot of formData when template was last loaded/saved — used for dirty comparison
  const initialFormDataRef = React.useRef(null);

  // Stable JSON stringify — sorts keys recursively so object key order doesn't matter
  const stableStringify = (val) => {
    if (val === null || typeof val !== 'object') return JSON.stringify(val);
    if (Array.isArray(val)) return '[' + val.map(stableStringify).join(',') + ']';
    const sorted = Object.keys(val).sort().map(k => JSON.stringify(k) + ':' + stableStringify(val[k]));
    return '{' + sorted.join(',') + '}';
  };

  // Fetch langs for LanguageSelector on mount
  useEffect(() => {
    const fetchLangs = async () => {
      setIsLoadingLangs(true);
      try {
        const resp = await axios.get(`${API_BASE_URL}/langs`, { headers });
        if (resp.data.status === 'success') {
          setLangsData(resp.data.langs || {});
        }
      } catch (e) {
        console.error('Error fetching langs:', e);
      } finally {
        setIsLoadingLangs(false);
      }
    };
    fetchLangs();
  }, []);

  // Auto dirty detection: compare current formData against initial snapshot
  useEffect(() => {
    if (!initialFormDataRef.current) return;
    const isDirty = stableStringify(formData) !== stableStringify(initialFormDataRef.current);
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [formData]);

  // submitFnRef gets populated after handleSubmit is defined (below)
  const submitFnRef = React.useRef(null);
  useImperativeHandle(ref, () => ({
    submitForm: () => submitFnRef.current?.(),
    getTemplateName: () => formData.template_name
  }));
 
   const API_BASE_URL = '/pceapi/genbi';
   const headers = {
     Authorization: GetCookie('access_token')
   };
 
      useEffect(() => {
     // Structures depend on the project's country — wait until a project is selected
     if (!formData.fabric_id) return;
     const fetchTableStructures = async () => {
       try {
         const resp = await axios.post(`${API_BASE_URL}/list-table-structures`, { fabric_id: formData.fabric_id }, { headers });
         if (resp.data.status === 'success' && resp.data.table_structures) {
           setTableStructures(resp.data.table_structures);
         }
       } catch (err) {
         console.error('Failed to fetch table structures:', err);
       }
     };
     fetchTableStructures();
   }, [formData.fabric_id]);

   const compareOrderingIds = (a, b) => {
     const aStr = a || '0';
     const bStr = b || '0';
     const aIsSummary = aStr.startsWith('S');
     const bIsSummary = bStr.startsWith('S');
     // Summary sections (S-series) always come before regular sections
     if (aIsSummary && !bIsSummary) return -1;
     if (!aIsSummary && bIsSummary) return 1;
     // Both summary: compare numeric part after 'S'
     const aClean = aIsSummary ? aStr.slice(1) : aStr;
     const bClean = bIsSummary ? bStr.slice(1) : bStr;
     const aParts = aClean.split('.').map(Number);
     const bParts = bClean.split('.').map(Number);
     for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
       const diff = (aParts[i] || 0) - (bParts[i] || 0);
       if (diff !== 0) return diff;
     }
     return 0;
   };

   // sections array to dictionary structure
   const convertSectionsToDict = (sectionsArray) => {
     const sectionsDict = {};
     const sectionOrder = [];
 
     sectionsArray.forEach(section => {
       const key = section.id || section.temp_id || `temp_${Date.now()}_${Math.random()}`;
       
       //agents array to dictionary
       const agentsDict = {};
       const agentOrder = [];
       
       if (section.agents) {
         section.agents.forEach(agent => {
           const agentKey = agent.id || agent.temp_id || `agent_temp_${Date.now()}_${Math.random()}`;
           agentsDict[agentKey] = { ...agent };
           if (!agent.isDeleted) {
             agentOrder.push(agentKey);
           }
         });
       }
      const oid = section.section_ordering_id || '';
      const isSPrefix = oid.startsWith('S') || oid.startsWith('s');
 
       sectionsDict[key] = {
         ...section,
         is_summary_section: section.is_summary_section || isSPrefix,
         agentsDict,
         agentOrder,
         vm_mappings: section.vm_mappings || []
       };
       

       if (!section.isDeleted) {
         sectionOrder.push(key);
       }
     });
 
     return { sectionsDict, sectionOrder };
   };
 
   // Convert dictionary structure back to array for API calls 
   const convertDictToSections = (sectionsDict, sectionOrder) => {
     return sectionOrder.map(sectionKey => {
       const section = sectionsDict[sectionKey];
       const agents = section.agentOrder.map(agentKey => {
         const agent = section.agentsDict[agentKey];
         // Return CLEAN agent object for CREATE API
         return {
           temp_id: agent.temp_id,
           agent_name: agent.agent_name || '',
           agent_context: agent.agent_context || '',
           additional_context: agent.additional_context || null,
           agent_ordering_id: agent.agent_ordering_id || 1,
           document_mappings: agent.document_mappings || [],
           reference_agent_ids: agent.reference_agent_ids || [],
           par_style: agent.par_style ?? null,
           table_structure_id: agent.table_structure_id ?? null,
           is_static_text: agent.table_structure_id ? false : (agent.is_static_text ?? false),
           static_text: agent.static_text ?? null,
           response: agent.response || null,
           parsed_response: agent.parsed_response || null,
           tags: agent.tags || null,
         };
       });
       
       // Return CLEAN section object for CREATE API
       return {
         temp_id: section.temp_id,
         section_name: section.section_name || '',
         parent_section_id: section.parent_section_id,
         section_ordering_id: section.section_ordering_id ?? '1',
         is_summary_section: section.is_summary_section || false,
         // null unless special: CHECK (is_summary_section OR special_section_config IS NULL)
         special_section_config: section.is_summary_section ? (section.special_section_config || null) : null,
         vm_mappings: section.vm_mappings || [],
         agents: agents
       };
     });
   };
 
   // Convert to structured format for UPDATE API - SEND ALL SECTIONS
   const convertToStructuredSections = (sectionsDict, sectionOrder) => {
     const structured = {
       new: [],
       modified: [],
       deleted: []
     };
 
     // Process sections in order
     sectionOrder.forEach(sectionKey => {
       const section = sectionsDict[sectionKey];
       
       // Prepare clean agents array
       const agents = section.agentOrder.map(agentKey => {
         const agent = section.agentsDict[agentKey];
         const isDirty = !agent.id || agent.isModified === true;
         const isStaticText = agent.table_structure_id ? false : (agent.is_static_text ?? false);
         // The form doesn't edit these, but update-template overwrites them on a dirty agent,
         // so send the loaded values back. Static text can't carry Extend/Amend (backend rejects it).
         const preserveResponse = !isStaticText && !!agent.preserve_response;
         const amendResponse = !isStaticText && !!agent.amend_response;
         return {
           id: agent.id,
           temp_id: agent.temp_id,
           agent_name: agent.agent_name || '',
           agent_context: agent.agent_context || '',
           additional_context: agent.additional_context || null,
           is_locked: agent.is_locked ?? false,
           agent_ordering_id: agent.agent_ordering_id || 1,
           document_mappings: agent.document_mappings || [],
           reference_agent_ids: agent.reference_agent_ids || [],
           par_style: agent.par_style ?? null,
           table_structure_id: agent.table_structure_id ?? null,
           is_static_text: isStaticText,
           static_text: agent.static_text ?? null,
           preserve_response: preserveResponse,
           amend_response: amendResponse,
           match_mapped_style: amendResponse && !!agent.match_mapped_style,
           response_colour: (preserveResponse || amendResponse) ? (agent.response_colour ?? null) : null,
           rich_formatting: !!agent.rich_formatting,
           is_dirty: isDirty,
           explicit_delete: false,
           response: agent.response || null,
           tags: agent.tags || null,
         };
       });

       Object.keys(section.agentsDict).forEach(agentKey => {
         const agent = section.agentsDict[agentKey];
         if (agent.isDeleted && agent.id) {
           agents.push({
             id: agent.id,
             agent_name: agent.agent_name || '',
             agent_context: agent.agent_context || '',
             additional_context: agent.additional_context || null,
             agent_ordering_id: agent.agent_ordering_id || 1,
             document_mappings: agent.document_mappings || [],
             reference_agent_ids: agent.reference_agent_ids || [],
             par_style: agent.par_style ?? null,
             is_dirty: false,
             explicit_delete: true
           });
         }
       });
 
       const sectionData = {
         id: section.id,
         temp_id: section.temp_id,
         section_name: section.section_name || '',
         parent_section_id: section.parent_section_id,
         section_ordering_id: section.section_ordering_id ?? '1',
         is_summary_section: section.is_summary_section || false,
         // null unless special: CHECK (is_summary_section OR special_section_config IS NULL)
         special_section_config: section.is_summary_section ? (section.special_section_config || null) : null,
         vm_mappings: section.vm_mappings || [],
         agents: agents
       };
 
       if (section.id) {
         // Existing section - always include in modified for safety
         // Backend will determine if actual UPDATE is needed
         structured.modified.push(sectionData);
       } else {
         // New section
         structured.new.push(sectionData);
       }
     });
 
     // Add deleted sections
     Object.values(sectionsDict).forEach(section => {
       if (section.isDeleted && section.id) {
         structured.deleted.push({
           id: section.id,
           
         });
       }
     });
 
     return structured;
   };
   // Generate hierarchical ordering ID
   const generateOrderingId = (parentKey, sectionsDict, sectionOrder) => {
     if (!parentKey) {
       // Root level - find max root number
       const rootSections = sectionOrder.filter(key => {
         const section = sectionsDict[key];
         return section && !section.parent_section_id;
       });
       const maxNumber = rootSections.reduce((max, key) => {
         const section = sectionsDict[key];
         const orderingId = section?.section_ordering_id || '0';
         const num = parseInt(orderingId.split('.')[0]);
         return Math.max(max, isNaN(num) ? 0 : num);
       }, 0);
        const fmt = formData.format_json?.numberingFormat;
const startVal = (fmt === "none" || fmt === undefined) ? (formData.format_json?.numberingStartValue ?? 1) : 1;
if (rootSections.length === 0) return String(startVal ?? 1);
return String(Math.max(maxNumber + 1, startVal ?? 1));
     } else {
       // Child level
       const parentSection = sectionsDict[parentKey];
       if (!parentSection) return '1';
       
       const parentOrderingId = parentSection.section_ordering_id;
       const siblings = sectionOrder.filter(key => {
         const section = sectionsDict[key];
         return section && section.parent_section_id === parentKey;
       });
       
       const maxChildNumber = siblings.reduce((max, key) => {
         const section = sectionsDict[key];
         const orderingId = section?.section_ordering_id || `${parentOrderingId}.0`;
         const parts = orderingId.split('.');
         const childNum = parseInt(parts[parts.length - 1]);
         return Math.max(max, isNaN(childNum) ? 0 : childNum);
       }, 0);
       
       return `${parentOrderingId}.${maxChildNumber + 1}`;
     }
   };
 
   // Find all descendant sections recursively
   const findAllDescendants = (parentKey, sectionsDict, sectionOrder) => {
     const descendants = [];
     
     if (!sectionsDict[parentKey]) return descendants;
     
     // Find direct children
     const directChildren = sectionOrder.filter(key => {
       const section = sectionsDict[key];
       return section && section.parent_section_id === parentKey;
     });
     
     directChildren.forEach(childKey => {
       descendants.push(childKey);
       // Recursively find grandchildren
       const grandChildren = findAllDescendants(childKey, sectionsDict, sectionOrder);
       descendants.push(...grandChildren);
     });
     
     return descendants;
   };
 
   // Recalculate ordering IDs only for affected siblings
   const recalculateSiblingOrderingIds = (parentKey, sectionsDict, sectionOrder, overrideFormatJson = null) => {
     // Safety checks
     if (!sectionsDict || typeof sectionsDict !== 'object' || !Array.isArray(sectionOrder)) {
       return sectionsDict;
     }
 
     // If parentKey is provided, ensure the parent section exists
     if (parentKey && !sectionsDict[parentKey]) {
       console.warn(`Parent section ${parentKey} not found during recalculation`);
       return sectionsDict;
     }
 
     const siblings = sectionOrder.filter(key => {
       const section = sectionsDict[key];
       if (!section || section.isDeleted) return false;
       
       return (parentKey ? section.parent_section_id === parentKey : !section.parent_section_id);
     });
 
     const updatedSectionsDict = { ...sectionsDict };

     // Skip summary sections (S-series) — never renumber them
     const regularSiblings = siblings.filter(key => {
       const sec = updatedSectionsDict[key];
       return !sec.is_summary_section && !(sec.section_ordering_id || '').startsWith('S');
     });

     regularSiblings.forEach((siblingKey, index) => {
       const section = updatedSectionsDict[siblingKey];
       
       // Safety check: ensure section exists
       if (!section) return;
       
       const newOrderingId = parentKey 
         ? `${updatedSectionsDict[parentKey]?.section_ordering_id || '0'}.${index + 1}`
         : (() => {
    const activeFormat = overrideFormatJson || formData.format_json;
    const fmt = activeFormat?.numberingFormat;
    const startVal = (fmt === "none" || fmt === undefined) ? (activeFormat?.numberingStartValue ?? 1) : 1;
    return String(startVal + index);
  })();
       
       if (section.section_ordering_id !== newOrderingId) {
         updatedSectionsDict[siblingKey] = {
           ...section,
           section_ordering_id: newOrderingId,
           isModified: section.id ? true : undefined
         };
 
         // Recursively update children - with safety checks
         const children = sectionOrder.filter(key => {
           const childSection = sectionsDict[key];
           return childSection && !childSection.isDeleted && childSection.parent_section_id === siblingKey;
         });
         
         if (children.length > 0) {
           try {
             const childUpdates = recalculateSiblingOrderingIds(siblingKey, updatedSectionsDict, sectionOrder, overrideFormatJson);
             if (childUpdates && typeof childUpdates === 'object') {
               Object.assign(updatedSectionsDict, childUpdates);
             }
           } catch (error) {
             console.error('Error updating children ordering:', error);
             // Continue without updating children if there's an error
           }
         }
       }
     });
 
     // SAFETY NET: forcibly restore every S-series section's ordering from original input
     Object.keys(sectionsDict).forEach(key => {
       const orig = sectionsDict[key];
       if (orig && (orig.is_summary_section || (orig.section_ordering_id || '').startsWith('S'))) {
         updatedSectionsDict[key] = { ...updatedSectionsDict[key], section_ordering_id: orig.section_ordering_id, is_summary_section: orig.is_summary_section };
       }
     });

     return updatedSectionsDict;
   };

   const recalculateSummaryOrderingIds = (sectionsDict, sectionOrder) => {
     const updatedSectionsDict = { ...sectionsDict };

     const isRootSummary = (sec) => {
       if (!sec || !isSummarySection(sec) || sec.isDeleted) return false;
       if (sec.parent_section_id) return false;
       const oid = sec.section_ordering_id || '';
       return !oid.includes('.');
     };

     const isChildOfSummary = (sec, parentKey) => {
       if (!sec || !isSummarySection(sec) || sec.isDeleted) return false;
       return sec.parent_section_id === parentKey;
     };

     const rootSummarySections = sectionOrder.filter(key => isRootSummary(updatedSectionsDict[key]));

     rootSummarySections.forEach((key, index) => {
       const newOrderingId = `S${index + 1}`;
       const section = updatedSectionsDict[key];
       if (section.section_ordering_id !== newOrderingId) {
         updatedSectionsDict[key] = {
           ...section,
           section_ordering_id: newOrderingId,
           isModified: section.id ? true : undefined
         };
       }

       const children = sectionOrder.filter(childKey => isChildOfSummary(updatedSectionsDict[childKey], key));

       children.forEach((childKey, childIndex) => {
         const childOrderingId = `${newOrderingId}.${childIndex + 1}`;
         const child = updatedSectionsDict[childKey];
         updatedSectionsDict[childKey] = {
           ...child,
           section_ordering_id: childOrderingId,
           isModified: child.id ? true : undefined
         };
       });
     });

     return updatedSectionsDict;
   };
 
   // Find correct insertion position for nested sections
   const findInsertionPosition = (parentKey, sectionOrder, sectionsDict) => {
     if (!parentKey) {
       return sectionOrder.length; // Add to end for root level
     }
 
     const parentIndex = sectionOrder.indexOf(parentKey);
     if (parentIndex === -1) return sectionOrder.length;
 
     // Find the last descendant of the parent
     let insertIndex = parentIndex + 1;
     const parentSection = sectionsDict[parentKey];
     if (!parentSection) return sectionOrder.length;
     
     const parentOrderingId = parentSection.section_ordering_id;
     
     for (let i = parentIndex + 1; i < sectionOrder.length; i++) {
       const sectionKey = sectionOrder[i];
       const section = sectionsDict[sectionKey];
       if (!section) continue;
       
       const orderingId = section.section_ordering_id;
       
       if (orderingId && orderingId.startsWith(parentOrderingId + '.')) {
         insertIndex = i + 1;
       } else {
         break;
       }
     }
     
     return insertIndex;
   };

   // Helper function to get all fabric documents
   const getAllFabricDocuments = (tags = null) => {
     const allDocIds = [];
     fabricTreeData.forEach(fabric => {
       fabric.threads?.forEach(thread => {
         thread.documents?.forEach(doc => {
           if (tags && tags.length > 0) {
             // Tag-based filtering: only include docs whose classification matches any of the agent's tags
             const docClassification = (doc.classification || '').trim().toLowerCase();
             const lowerTags = tags.map(t => t.trim().toLowerCase());
             if (lowerTags.includes(docClassification)) {
               allDocIds.push(doc.id);
             }
           } else {
             allDocIds.push(doc.id);
           }
         });
       });
     });
     return allDocIds.map(docId => ({
       doc_id: docId,
       selected: true
     }));
   };

   // Load template data for editing
   useEffect(() => {
  if (editingTemplate) {
    setIsInitialFabricLoad(true);
    setIsDocGeneratedImport(false);
    loadTemplateDetails();
  } else {
    // Reset form for create mode
    setFormData({
      template_name: '',
      fabric_id: initialFabricId || null,
      fabric_name: '',
      sectionsDict: {},
      sectionOrder: [],
      vm_config: false,
      lang_id: 1,
      format_json: DEFAULT_REPORT_CONFIG,
      global_prompt: ''
    });
    initialFormDataRef.current = null;

    // Reset value message state
    setUseValueMessage(false);
    setIsInitialFabricLoad(false);

    setIsDocGeneratedImport(false);
    // setSourceDocId(null);
    setShowResponsePanel({});
    setImportedLibraryTemplateId(null);
    setImportedLibraryTemplateName('');
    setHeadingMappingId(null);
    setSavedMapping(null);
    // Reset language display to English default
    setSelectedLanguage({ id: 1, language_name: 'English' });
    // Pre-fill fabric documents same way edit fetches template details by fabric_id
    if (initialFabricId) {
      setIsLoading(true);
      loadFabricDocuments(initialFabricId).finally(() => {
        setIsLoading(false);
        if (onProcessingEnd) onProcessingEnd();
      });
    } else {
      if (onProcessingEnd) onProcessingEnd();
    }

  }
}, [editingTemplate]); // Note: languages added as dependency
 
   // Load fabric documents when fabric changes
   useEffect(() => {
     if (formData.fabric_id && fabricTreeData.length > 0) {
       // Skip update ONLY for update mode + initial load
       // All other cases should update:
       // - Create mode + initial selection
       // - Create mode + fabric change  
       // - Update mode + fabric change
       if (!(editingTemplate && isInitialFabricLoad)) {
         updateAllAgentsWithFabricDocuments();
       }
       
       //After initial load is processed, reset flag
       if (isInitialFabricLoad) {
         setIsInitialFabricLoad(false);
       }
     }
   }, [fabricTreeData]);

   // Backfill fabric_name for a pre-selected create-mode fabric (initialFabricId) once
   // its documents load — fabric_unstructured_data_tree returns the fabric's name for free
   useEffect(() => {
     if (!editingTemplate && !formData.fabric_name && fabricTreeData[0]?.name) {
       setFormData(prev => ({ ...prev, fabric_name: fabricTreeData[0].name }));
     }
   }, [fabricTreeData]);
 // Set language after both template and languages are loaded
   const loadTemplateDetails = async () => {
     if (!editingTemplate) return;
     setIsLoading(true);
     try {
       const response = await axios.post(
         `${API_BASE_URL}/get-template-details`,
         {
           template_id: editingTemplate.id,
           fabric_id: editingTemplate.fabric_id
         },
         { headers }
       );
 
       if (response.data.status === 'success') {
         const templateData = response.data.data;
         setCapabilities(templateData.capabilities || {});
         let { sectionsDict, sectionOrder } = convertSectionsToDict(templateData.sections || []);
         sectionsDict = recalculateSummaryOrderingIds(sectionsDict, sectionOrder);
         const cur_lang_id = templateData.lang_id ?? 1;
         setFormData({
           template_name: templateData.template_name,
           fabric_id: templateData.fabric_id,
           fabric_name: templateData.fabric_name,
           sectionsDict,
           sectionOrder,
           vm_config: templateData.vm_config,
           lang_id: cur_lang_id,
           format_json: templateData.format_json || DEFAULT_REPORT_CONFIG,
           global_prompt: templateData.global_prompt || ''
         });
         // Snapshot for dirty detection
         initialFormDataRef.current = {
           template_name: templateData.template_name,
           fabric_id: templateData.fabric_id,
           fabric_name: templateData.fabric_name,
           sectionsDict,
           sectionOrder,
           vm_config: templateData.vm_config,
           lang_id: cur_lang_id,
           format_json: templateData.format_json || DEFAULT_REPORT_CONFIG,
           global_prompt: templateData.global_prompt || ''
         };
         // Set selected language based on template data

         // Resolve language name for display using langs API
         try {
           const langsResp = await axios.get(`${API_BASE_URL}/langs`, { headers });
           if (langsResp.data.status === 'success') {
             const langsObj = langsResp.data.langs || {};
             const langName = langsObj[cur_lang_id] || langsObj[String(cur_lang_id)] || 'English';
             setSelectedLanguage({ id: cur_lang_id, language_name: langName });
           }
         } catch {
           setSelectedLanguage({ id: cur_lang_id, language_name: 'English' });
         }
         setHeadingMappingId(templateData.heading_mapping_id || null);
         setUseValueMessage(templateData.vm_config);
         setIsLoading(false);

        endProcessing();
         if (templateData.vm_config && templateData.vm_config_data) {
         setVmConfiguration(templateData.vm_config_data);
         }
         if (templateData.fabric_id) {
           // Load fabric documents - the useEffect will handle not updating agents
           await loadFabricDocuments(templateData.fabric_id);
         }
       } else {
         endProcessing();
         onError(response.data.message || 'Failed to load Preset details');
       }
     } catch (error) {
      setIsLoading(false);
       endProcessing();
       onError('Failed to load Preset details');
     }
   };
 
   // Always preserve existing sections during fabric changes
   const handleFabricSelection = async (fabricId, fabricName) => {
     setFormData(prev => ({
       ...prev,
       fabric_id: fabricId,
       fabric_name: fabricName,
       sectionsDict: prev.sectionsDict,  // Always preserve sections
       sectionOrder: prev.sectionOrder   // Always preserve order
     }));
 
     if (fabricId) {
       //  User changed fabric, not initial load
       setIsInitialFabricLoad(false);
       await loadFabricDocuments(fabricId);
     }
   };
    // Called by AgentTranslationModal: langId is number, langName resolved from modal's internal state
    const handleTranslateSelect = (langId, langName) => {
      setSelectedLanguage({ id: langId, language_name: langName || 'Language ' + langId });
      setFormData(prev => ({ ...prev, lang_id: langId }));
      setIsLangModalOpen(false);
    };
   const loadFabricDocuments = async (fabricId) => {
     try {
       const response = await axios.post(
         `${API_BASE_URL}/fabric_unstructured_data_tree`,
         {
           fabric_id: fabricId,        
           include_documents: true,
           page: 1,                   
           size: 25                  
         },
         { headers }
       );
 
       if (response.data.status === 'success') {
         setFabricTreeData(response.data.data);
       }
     } catch (error) {
       console.error('Error loading project documents:', error);
     }
   };
 
   const updateAllAgentsWithFabricDocuments = () => {
     if (!fabricTreeData.length || !formData.fabric_id) return;
 
    //  const allDocIds = [];
    //  fabricTreeData[0].threads?.forEach(thread => {
    //    thread.documents?.forEach(doc => {
    //      allDocIds.push(doc.id);
    //    });
    //  });
 
    //  const newDocumentMappings = allDocIds.map(docId => ({
    //    doc_id: docId,
    //    selected: true
    //  }));

      // Pre-compute all-docs mapping for agents without tags
      const allDocumentMappings = getAllFabricDocuments();
      setFormData(prev => {
       // Create a completely new sectionsDict to avoid nested spreading
       const updatedSectionsDict = {};
       
       // Process all sections in a single loop
       Object.keys(prev.sectionsDict).forEach(sectionKey => {
         const section = prev.sectionsDict[sectionKey];
         
         // Create new agentsDict for this section
         const updatedAgentsDict = {};
         
         // Process all agents in this section
         Object.keys(section.agentsDict).forEach(agentKey => {
           const agent = section.agentsDict[agentKey];

           // Tag-based mapping: if agent has tags, only map matching docs
           const agentMappings = agent.tags?.length
             ? getAllFabricDocuments(agent.tags)
             : allDocumentMappings;
           
           // Create new agent object with updated document mappings
           updatedAgentsDict[agentKey] = {
             ...agent,
             document_mappings: agentMappings
           };
         });
         
         // Create new section object
         updatedSectionsDict[sectionKey] = {
           ...section,
           agentsDict: updatedAgentsDict
         };
       });
       return {
         ...prev,
         sectionsDict: updatedSectionsDict
       };
     });
   };
 
   // Add new section
   const addSection = (parentKey = null) => {
     const newKey = `temp_${Date.now()}_${Math.random()}`;
     const newOrderingId = generateOrderingId(parentKey, formData.sectionsDict, formData.sectionOrder);
     const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);
     
     const newSection = {
       temp_id: newKey,
       section_name: '',
       parent_section_id: parentKey,
       section_ordering_id: newOrderingId,
       agentsDict: {},
       agentOrder: [],
       isNew: true
     };
 
     setFormData(prev => {
       const newSectionOrder = [...prev.sectionOrder];
       newSectionOrder.splice(insertPosition, 0, newKey);
       
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [newKey]: newSection
         },
         sectionOrder: newSectionOrder
       };
     });
   };
 
   // Add new agent to section
   const addAgent = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return;
     
     const newAgentKey = `agent_temp_${Date.now()}_${Math.random()}`;
     const maxOrderingId = section.agentOrder.length > 0 
       ? Math.max(...section.agentOrder.map(key => 
           section.agentsDict[key]?.agent_ordering_id || 0
         ))
       : 0;
 
     // Get all document IDs for default selection
     const allDocIds = [];
     fabricTreeData.forEach(fabric => {
       fabric.threads?.forEach(thread => {
         thread.documents?.forEach(doc => {
           allDocIds.push(doc.id);
         });
       });
     });
 
     const newAgent = {
       temp_id: newAgentKey,
       agent_name: '',
       agent_context: '',
       additional_context: null,
       agent_ordering_id: maxOrderingId + 1,
       document_mappings: allDocIds.map(docId => ({
         doc_id: docId,
         selected: true
       })),
       reference_agent_ids: [],
       par_style: null,
       table_structure_id: null,       
       is_static_text: false,
       static_text: null,
       isNew: true
     };
 
     setFormData(prev => ({
       ...prev,
       sectionsDict: {
         ...prev.sectionsDict,
         [sectionKey]: {
           ...prev.sectionsDict[sectionKey],
           agentsDict: {
             ...prev.sectionsDict[sectionKey].agentsDict,
             [newAgentKey]: newAgent
           },
           agentOrder: [...prev.sectionsDict[sectionKey].agentOrder, newAgentKey],
           hasModifiedAgents: true  // Mark section as having modified agents
         }
       }
     }));
   };
 
   // Delete section
   const deleteSection = (sectionKey) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       let updatedSectionsDict = { ...prev.sectionsDict };
       let updatedSectionOrder = [...prev.sectionOrder];
 
       // Find all descendants that need to be deleted
       const allDescendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);
       
       // Delete/mark descendants and parent
       const sectionsToRemove = [sectionKey, ...allDescendants];
       sectionsToRemove.forEach(keyToRemove => {
         const sectionToDelete = updatedSectionsDict[keyToRemove];
         if (sectionToDelete) {
           if (sectionToDelete.id) {
             // Existing section - mark as deleted
             updatedSectionsDict[keyToRemove] = {
               ...sectionToDelete,
               isDeleted: true
             };
           } else {
             // New section - remove completely
             delete updatedSectionsDict[keyToRemove];
           }
         }
       });
       
       // Remove from order
       updatedSectionOrder = updatedSectionOrder.filter(key => 
         !sectionsToRemove.includes(key)
       );
 
       // Recalculate ordering for siblings at the same level as the deleted parent
       const parentKey = section.parent_section_id;
       try {
         updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, updatedSectionOrder);
       } catch (error) {
         console.error('Error recalculating sibling ordering:', error);
       }

       if (isSummarySection(section)) {
         try {
           updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
         } catch (error) {
           console.error('Error recalculating summary ordering:', error);
         }
       }

       return {
         ...prev,
         sectionsDict: updatedSectionsDict,
         sectionOrder: updatedSectionOrder
       };
     });
   };
 
   // Delete agent
   const deleteAgent = (sectionKey, agentKey) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       const agent = section.agentsDict[agentKey];
       if (!agent) return prev;
       
       let updatedAgentsDict = { ...section.agentsDict };
       let updatedAgentOrder = [...section.agentOrder];
 
       if (agent.id) {
         // Existing agent - mark as deleted
         updatedAgentsDict[agentKey] = {
           ...agent,
           isDeleted: true
         };
       } else {
         // New agent - remove completely
         delete updatedAgentsDict[agentKey];
       }
 
       // Remove from order
       updatedAgentOrder = updatedAgentOrder.filter(key => key !== agentKey);
 
       // Recalculate agent ordering IDs only within this section
       updatedAgentOrder.forEach((agentKey, index) => {
         const agent = updatedAgentsDict[agentKey];
         if (agent && agent.agent_ordering_id !== index + 1) {
           updatedAgentsDict[agentKey] = {
             ...agent,
             agent_ordering_id: index + 1,
             isModified: agent.id ? true : undefined
           };
         }
       });
 
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             agentsDict: updatedAgentsDict,
             agentOrder: updatedAgentOrder,
             hasModifiedAgents: true  // Mark section as having modified agents
           }
         }
       };
     });
   };
 
   // Update section field
   const updateSectionField = (sectionKey, field, value) => {
     setFormData(prev => ({
       ...prev,
       sectionsDict: {
         ...prev.sectionsDict,
         [sectionKey]: {
           ...prev.sectionsDict[sectionKey],
           [field]: value,
           isModified: prev.sectionsDict[sectionKey].id ? true : undefined
         }
       }
     }));
   };
 
   const updateSpecialSectionConfig = (sectionKey, configKey, value) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       const currentConfig = section.special_section_config || {};
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             special_section_config: { ...currentConfig, [configKey]: value },
             isModified: section.id ? true : undefined
           }
         }
       };
     });
   };

   useEffect(() => {
     const handleSpecialConfigClickOutside = (event) => {
       if (specialSectionConfigRef.current && !specialSectionConfigRef.current.contains(event.target)) {
         setOpenSpecialSectionConfig(null);
       }
     };
     const handleSpecialConfigScroll = () => setOpenSpecialSectionConfig(null);
     document.addEventListener("mousedown", handleSpecialConfigClickOutside);
     document.addEventListener("scroll", handleSpecialConfigScroll, true);
     return () => {
       document.removeEventListener("mousedown", handleSpecialConfigClickOutside);
       document.removeEventListener("scroll", handleSpecialConfigScroll, true);
     };
   }, []);

   // Update agent field
   const updateAgentField = (sectionKey, agentKey, field, value) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       const agent = section.agentsDict[agentKey];
       
       return {
         ...prev,
         sectionsDict: {
           ...prev.sectionsDict,
           [sectionKey]: {
             ...section,
             agentsDict: {
               ...section.agentsDict,
               [agentKey]: {
                 ...agent,
                 [field]: value,
                 isModified: agent.id ? true : undefined
               }
             },
             hasModifiedAgents: true  // Mark section as having modified agents
           }
         }
       };
     });
   };
 
   // Open document selector for agent
   const openDocumentSelector = (sectionKey, agentKey) => {
     setCurrentAgentIndex({ sectionKey, agentKey });
     setDocumentSelectorOpen(true);
   };
   const openConfigureModal = (sectionKey, agentKey) => {
    setAgentToConfigure({ sectionKey, agentKey });
    setAgentConfigureOpen(true);
  };

   const closeAgentConfigure = () => {
    setAgentConfigureOpen(false);
  };
  const openReportFormatting = () => {
  setShowReportFormatting(true);
};

    const handleReportFormattingSave = (formattingData) => {
    setFormData(prev => {
        const fmt = formattingData?.numberingFormat;

        // Snapshot all S-series sections before any recalculation
        const sSectionSnapshots = {};
        Object.keys(prev.sectionsDict).forEach(key => {
          const sec = prev.sectionsDict[key];
          if (sec && (sec.is_summary_section || (sec.section_ordering_id || '').startsWith('S'))) {
            sSectionSnapshots[key] = { section_ordering_id: sec.section_ordering_id, is_summary_section: sec.is_summary_section };
          }
        });

        let newSectionsDict = prev.sectionsDict;
        if (fmt === "none") {
          newSectionsDict = recalculateSiblingOrderingIds(null, prev.sectionsDict, prev.sectionOrder, formattingData);
        }

        // Forcibly restore S-series — no matter what happened above
        Object.keys(sSectionSnapshots).forEach(key => {
          if (newSectionsDict[key]) {
            newSectionsDict = { ...newSectionsDict, [key]: { ...newSectionsDict[key], ...sSectionSnapshots[key] } };
          }
        });

        return { ...prev, format_json: formattingData, sectionsDict: newSectionsDict };
    });
    closeReportFormatting();
    };

    const closeReportFormatting = () => {
      setShowReportFormatting(false);
    };
 
   // Handle document selection for agent
   const handleDocumentSelection = (selectedDocIds) => {
     const { sectionKey, agentKey } = currentAgentIndex;
     const documentMappings = selectedDocIds.map(docId => ({
       doc_id: docId,
       selected: true
     }));
 
     updateAgentField(sectionKey, agentKey, 'document_mappings', documentMappings);
     setDocumentSelectorOpen(false);
   };
 
   // Helper: check if a section is S-series (summary) — used to block reordering
   const isSummarySection = (section) => {
     if (!section) return false;
     if (section.is_summary_section) return true;
     const oid = (section.section_ordering_id || '');
     return oid.startsWith('S') || oid.startsWith('s');
   };

   // Move section up/down within the same parent level
   const moveSectionOrder = (sectionKey, direction) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;

       const sectionIsSummary = isSummarySection(section);
       const parentKey = section.parent_section_id;

       // Get siblings at the same level — only same type (S-series with S-series, ordinary with ordinary)
       const siblings = prev.sectionOrder.filter(key => {
         const siblingSection = prev.sectionsDict[key];
         if (!siblingSection) return false;
         const siblingIsSummary = isSummarySection(siblingSection);
         // Must be same type (both summary or both ordinary)
         if (siblingIsSummary !== sectionIsSummary) return false;
         return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
       });
       
       const currentSiblingIndex = siblings.indexOf(sectionKey);
       const targetSiblingIndex = direction === 'up' ? currentSiblingIndex - 1 : currentSiblingIndex + 1;
       
       // Check if move is valid
       if (targetSiblingIndex < 0 || targetSiblingIndex >= siblings.length) {
         return prev;
       }
       
       const targetSectionKey = siblings[targetSiblingIndex];
       
       // Find all descendants of both sections (current and target)
       const currentDescendants = findAllDescendants(sectionKey, prev.sectionsDict, prev.sectionOrder);
       const targetDescendants = findAllDescendants(targetSectionKey, prev.sectionsDict, prev.sectionOrder);
       
       // Create new section order
       let newSectionOrder = [...prev.sectionOrder];
       
       // Remove current section and all its descendants
       const currentGroup = [sectionKey, ...currentDescendants];
       newSectionOrder = newSectionOrder.filter(key => !currentGroup.includes(key));
       
       // Find where to insert the moved section group
       const targetGroup = [targetSectionKey, ...targetDescendants];
       let insertIndex;
       
       if (direction === 'up') {
         // Insert before the target section
         insertIndex = newSectionOrder.indexOf(targetSectionKey);
       } else {
         // Insert after the target section and all its descendants
         const lastTargetIndex = targetGroup.reduce((maxIndex, key) => {
           const keyIndex = newSectionOrder.indexOf(key);
           return keyIndex > maxIndex ? keyIndex : maxIndex;
         }, -1);
         insertIndex = lastTargetIndex + 1;
       }
       
       // Insert the current section group at the new position
       newSectionOrder.splice(insertIndex, 0, ...currentGroup);

       // Recalculate ordering IDs
       let updatedSectionsDict = { ...prev.sectionsDict };
       try {
         if (sectionIsSummary) {
           // Recalculate S-series numbering
           updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, newSectionOrder);
           // Deep-renumber children for each S-root
           const sRoots = newSectionOrder.filter(key => {
             const sec = updatedSectionsDict[key];
             return sec && isSummarySection(sec) && !sec.parent_section_id;
           });
           sRoots.forEach(rootKey => {
             assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, newSectionOrder);
           });
         } else {
           updatedSectionsDict = recalculateSiblingOrderingIds(parentKey, updatedSectionsDict, newSectionOrder);
         }
       } catch (error) {
         console.error('Error recalculating section ordering:', error);
         return prev;
       }
 
       return {
         ...prev,
         sectionsDict: updatedSectionsDict,
         sectionOrder: newSectionOrder
       };
     });
   };
 
   // Helper function to check if section can be moved up/down
   const canMoveSectionUp = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return false;

     const sectionIsSummary = isSummarySection(section);
     const parentKey = section.parent_section_id;
     const siblings = formData.sectionOrder.filter(key => {
       const siblingSection = formData.sectionsDict[key];
       if (!siblingSection) return false;
       if (isSummarySection(siblingSection) !== sectionIsSummary) return false;
       return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
     });

     return siblings.indexOf(sectionKey) > 0;
   };

   const canMoveSectionDown = (sectionKey) => {
     const section = formData.sectionsDict[sectionKey];
     if (!section) return false;

     const sectionIsSummary = isSummarySection(section);
     const parentKey = section.parent_section_id;
     const siblings = formData.sectionOrder.filter(key => {
       const siblingSection = formData.sectionsDict[key];
       if (!siblingSection) return false;
       if (isSummarySection(siblingSection) !== sectionIsSummary) return false;
       return parentKey ? siblingSection.parent_section_id === parentKey : !siblingSection.parent_section_id;
     });

     return siblings.indexOf(sectionKey) < siblings.length - 1;
   };

  const beginProcessing = (message) => {
    setBlockingOverlayCount(count => count + 1);
    if (onProcessingStart) onProcessingStart(message);
  };

  const endProcessing = () => {
    setBlockingOverlayCount(count => Math.max(0, count - 1));
    if (onProcessingEnd) onProcessingEnd();
  };

  const handleImportFullScreenChange = (isFullScreen) => {
    setBlockingOverlayCount(count =>
      isFullScreen ? count + 1 : Math.max(0, count - 1)
    );
  };

  const isSectionTreeCompact =
    blockingOverlayCount > 0 || !!savedMapping || showReportFormatting;

   const moveAgentOrder = (sectionKey, agentKey, direction) => {
     setFormData(prev => {
       const section = prev.sectionsDict[sectionKey];
       if (!section) return prev;
       
       const currentIndex = section.agentOrder.indexOf(agentKey);
       const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
       
       if (targetIndex >= 0 && targetIndex < section.agentOrder.length) {
         const newAgentOrder = [...section.agentOrder];
         [newAgentOrder[currentIndex], newAgentOrder[targetIndex]] = 
         [newAgentOrder[targetIndex], newAgentOrder[currentIndex]];
         
         // Update ordering IDs
         const updatedAgentsDict = { ...section.agentsDict };
         newAgentOrder.forEach((agentKey, index) => {
           const agent = updatedAgentsDict[agentKey];
           if (agent) {
             updatedAgentsDict[agentKey] = {
               ...agent,
               agent_ordering_id: index + 1,
               isModified: agent.id ? true : undefined
             };
           }
         });
 
         return {
           ...prev,
           sectionsDict: {
             ...prev.sectionsDict,
             [sectionKey]: {
               ...section,
               agentsDict: updatedAgentsDict,
               agentOrder: newAgentOrder,
               hasModifiedAgents: true  // Mark section as having modified agents
             }
           }
         };
       }
       
       return prev;
     });
   };
 
   // Handle form submission
   const handleSubmit = async (e) => {
     e.preventDefault();
 
     if (!formData.template_name.trim()) {
       onError('Preset name is required');
       return;
     }
 
     if (!formData.fabric_id) {
       onError('Please select a project');
       return;
     }
 
 
     if (formData.sectionOrder.length === 0) {
       onError('At least one section is required');
       return;
     }
 
     setIsLoading(true);
     
     const processingMessage = editingTemplate ? 'Updating Preset' : 'Creating Preset';
     beginProcessing(processingMessage);
 
     try {
       const endpoint = editingTemplate ? '/update-template' : '/create-template';
       
       let requestData;
       
       if (editingTemplate) {
         // UPDATE: Use structured format
         const structuredSections = convertToStructuredSections(formData.sectionsDict, formData.sectionOrder);
         
         requestData = {
           template_id: editingTemplate.id,
           template_name: formData.template_name,
           fabric_id: formData.fabric_id,
           vm_config: formData.vm_config || false,
           sections: structuredSections,
           lang_id: formData.lang_id || 1,
           format_json: withTocDefaults(formData.format_json) || null,
           global_prompt: formData.global_prompt || null,
           frontpage: formData.frontpage || null
         };
       } else {
         // CREATE: Use array format
         const sectionsArray = convertDictToSections(formData.sectionsDict, formData.sectionOrder);
         
         requestData = {
           template_name: formData.template_name,
           fabric_id: formData.fabric_id,
           vm_config: formData.vm_config || false,
           sections: sectionsArray,
           lang_id: formData.lang_id || 1,
           format_json: withTocDefaults(formData.format_json) || null,
           global_prompt: formData.global_prompt || null,
           frontpage: formData.frontpage || null,
           source_doc_id: formData.source_doc_id || null,
           heading_mapping_id: formData.heading_mapping_id || null,           
         };
       }
       if (formData.vm_config) {
         if (!vmConfiguration || !vmConfiguration.stakeholder_requirement) {
           onError('Value Message configuration is enabled but the stakeholder requirement is missing.');
           setIsLoading(false);
           endProcessing();
           return;
         }
         requestData.vm_config_data = vmConfiguration;
       }
       
       console.log('Sending request data:', JSON.stringify(requestData, null, 2)); // Debug log
 
       // Both endpoints stream NDJSON (heartbeat while the save runs, to survive the
       // gateway idle timeout), so this reads the stream and takes the final envelope.
       const { data: result } = await postNdjson(
         `${API_BASE_URL}${endpoint}`,
         requestData,
         headers
       );
 
       if (result.status === 'NotAuthorized') {
         authNavigate('/login', 'iam', navigate);
         return;
       }
 
       if (result.status === 'failed') {
         onError(result.message);
         return;
       }
 
       // Update snapshot so dirty resets after save
       initialFormDataRef.current = JSON.parse(JSON.stringify(formData));
       onSuccess(result.message, formData.lang_id, formData.template_name);
     } catch (error) {
       console.error('Error saving template:', error);
       onError(error.message || 'Failed to save Preset');
     } finally {
       setIsLoading(false);
       endProcessing();
     }
   };
  submitFnRef.current = () => handleSubmit({ preventDefault: () => {} });
 
   const handleValueMessageToggle = (e) => {
     const isEnabled = e.target.checked;
     setUseValueMessage(isEnabled);
     setFormData(prev => ({
       ...prev,
       vm_config: isEnabled
     }));
 
     if (isEnabled && !vmConfiguration) {
      //  const allDocIds = [];
      //  fabricTreeData.forEach(fabric => {
      //    fabric.threads?.forEach(thread => {
      //      thread.documents?.forEach(doc => {
      //        allDocIds.push(doc.id);
      //      });
      //    });
      //  });
      const allDocIds = getAllFabricDocuments();
       setVmConfiguration({
         stakeholder_requirement: '',
         categories: [],
         selected_doc_ids: allDocIds.map(m => m.doc_id),
       });
     }
   };
   const openValueMessageModal = () => {
     if (!formData.fabric_id) {
       return;
     }
     setValueMessageModalOpen(true);
   };
   const handleSaveVmConfiguration = (config) => {
     setVmConfiguration(config);
     setValueMessageModalOpen(false);
   };
   const closeValueMessageModal = () => {
     setValueMessageModalOpen(false);
   };

  const getParagraphStyleOptions = () => {
    const paragraphConfigs = Array.isArray(formData.format_json?.paragraph)
      ? formData.format_json.paragraph
      : [];

    const customParagraphs = paragraphConfigs.filter(
      (p) => (p?.id || "default") !== "default"
    );

    return customParagraphs.map((p, index) => ({
      label: p.name || `Paragraph (Style ${index + 1})`,
      value: p.level ?? (index + 1),
    }));
  };

  const toggleParagraphDropdown = (agentKey) => {
    setOpenParagraphDropdown((prev) => (prev === agentKey ? null : agentKey));
  };

  const closeParagraphDropdown = () => {
    setOpenParagraphDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeParagraphDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []); 

   const addTempIdsToSections = (sectionsArray) => {
    if (!sectionsArray || sectionsArray.length === 0) return [];

    const orderingIdToTempIdMap = {};
    const idToTempIdMap = {};
    const sectionsWithTempIds = [];

    // Pass 1: Create a fresh temp_id for each section and map BOTH its
    sectionsArray.forEach(section => {
      const temp_id = `temp_${Date.now()}_${Math.random()}`;
      if (section.section_ordering_id != null) {
        orderingIdToTempIdMap[String(section.section_ordering_id)] = temp_id;
      }
      if (section.id != null) {
        idToTempIdMap[String(section.id)] = temp_id;
      }
      sectionsWithTempIds.push({ ...section, temp_id });
    });

    // Pass 2: Resolve parent_section_id to the parent's new temp_id and add temp_ids to agents
    return sectionsWithTempIds.map(section => {
      // Look up the parent's temp_id using its ordering_id
      let parentTempId = null;
      const orderingId =
        section.section_ordering_id != null ? String(section.section_ordering_id) : '';
      if (orderingId.includes('.')) {
        const parentOrderingId = orderingId.split('.').slice(0, -1).join('.');
        parentTempId = orderingIdToTempIdMap[parentOrderingId] || null;
      }
      if (!parentTempId && section.parent_section_id != null) {
        const rawParent = String(section.parent_section_id);
        parentTempId =
          idToTempIdMap[rawParent] || orderingIdToTempIdMap[rawParent] || null;
      }
      const agentsWithTempIds = (section.agents || []).map(agent => {
        const { id: _staleAgentId, ...agentRest } = agent;
        return {
          ...agentRest,
          temp_id: `agent_temp_${Date.now()}_${Math.random()}`,
          par_style: agent.par_style ?? null
        };
      });
      const { id: _staleSectionId, ...sectionRest } = section;

      return {
        ...sectionRest,
        parent_section_id: parentTempId,
        agents: agentsWithTempIds
      };
    });
  };

  // Turns each agent's preset `refer_agents` into reference_agent_ids (temp ids),
  // then flags (but keeps) circular references. Entries are matched on
  // section_ordering_id + agent_ordering_id; agent_name alone is a fallback (must
  // be unique in that section) and otherwise only cross-checks the match.
  // Mutates the agents in place; returns warnings for the import modal.
  const resolveImportedReferAgents = (sectionsArray) => {
    const warnings = [];
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const byOrder = {};
    const byName = {};
    const allAgents = [];
    const labels = new Map();

    sectionsArray.forEach(section => {
      const sid = String(section.section_ordering_id ?? '');
      (section.agents || []).forEach(agent => {
        allAgents.push(agent);
        labels.set(agent, `S.${sid} Agent ${agent.agent_ordering_id} - ${agent.agent_name || ''}`);
        byOrder[`${sid}:${agent.agent_ordering_id}`] = agent;
        const nameKey = `${sid}:${norm(agent.agent_name)}`;
        if (!byName[nameKey]) byName[nameKey] = [];
        byName[nameKey].push(agent);
      });
    });
    const labelOf = (a) => labels.get(a) || '?';

    allAgents.forEach(agent => {
      const ids = [];
      (agent.refer_agents || []).forEach(ref => {
        const sid = String(ref?.section_ordering_id ?? '');
        const refText = `S.${sid}${ref?.agent_ordering_id != null ? ` Agent ${ref.agent_ordering_id}` : ''}${ref?.agent_name ? ` - ${ref.agent_name}` : ''}`;
        let target = ref?.agent_ordering_id != null ? byOrder[`${sid}:${ref.agent_ordering_id}`] : null;
        if (!target && ref?.agent_name) {
          const hits = byName[`${sid}:${norm(ref.agent_name)}`] || [];
          if (hits.length === 1) target = hits[0];
        }
        if (!target) {
          warnings.push(`${labelOf(agent)} → ${refText}: skipped (agent not found)`);
          return;
        }
        if (ref.agent_name && norm(ref.agent_name) !== norm(target.agent_name)) {
          warnings.push(`${labelOf(agent)} → ${refText}: linked to "${target.agent_name}" (name mismatch)`);
        }
        ids.push(agentRefId(target));
      });
      // Any reference_agent_ids in the file are stale db ids — never carry them over.
      agent.reference_agent_ids = ids;
      delete agent.refer_agents;
    });

    return [...warnings, ...flagReferenceCycles(allAgents, labelOf)];
  };

const performImport = ({
    sections: sectionsToImport,
    name: templateName,
    format_json: importedFormat,
    frontpage: importedFrontpage,
    global_prompt: importedGlobalPrompt = null,
    source_doc_id: importedSourceDocId = null,
    heading_mapping_id: importedHeadingMappingId = null,    
    doc_generated: isDocGenerated = false,
    source_lang_id: importedSourceLangId = null,
    source_lang_name: importedSourceLangName = null,
    // A library preset carries its language on the template JSON and the API
    // resolves it to an id; a doc import sends the detected one as source_lang_*.
    // Only one pair is ever present.
    lang_id: importedLangId = null,
    language: importedLangName = null,
  }) => {
  const resolvedLangId = importedSourceLangId || importedLangId;
  const resolvedLangName = importedSourceLangName || importedLangName;
  if (onProcessingStart) {
    onProcessingStart('Importing Preset structure...');
  }

  // 1. Add temp_ids to the imported structure
  let sectionsWithTempIds = addTempIdsToSections(sectionsToImport);
  const referWarnings = resolveImportedReferAgents(sectionsWithTempIds);
  setImportReferWarnings(isDocGenerated ? [] : referWarnings);

  // 2. NEW LOGIC: Check if Fabric is already selected. If so, select all documents automatically.
  //   if (isDocGenerated) {
  //     const sourceDocumentMappings = importedSourceDocId
  //       ? [{ doc_id: importedSourceDocId, selected: true }]
  //       : [];

  //     sectionsWithTempIds = sectionsWithTempIds.map(section => ({
  //       ...section,
  //       agents: (section.agents || []).map(agent => ({
  //         ...agent,
  //         document_mappings: sourceDocumentMappings.length > 0
  //           ? sourceDocumentMappings.map(mapping => ({ ...mapping }))
  //           : (agent.document_mappings || []),
  //         par_style: agent.par_style ?? null
  //       }))
  //     }));
  //   } else if (formData.fabric_id && fabricTreeData.length > 0) {

  //   // Collect all document IDs from the currently loaded fabric
  //   const allDocIds = [];
  //   fabricTreeData.forEach(fabric => {
  //     fabric.threads?.forEach(thread => {
  //       thread.documents?.forEach(doc => {
  //         allDocIds.push(doc.id);
  //       });
  //     });
  //   });

  //   // Create the "all selected" mapping
  //   const newDocumentMappings = allDocIds.map(docId => ({
  //     doc_id: docId,
  //     selected: true
  //   }));

  //   // Inject these mappings into every agent in every imported section
  //   sectionsWithTempIds = sectionsWithTempIds.map(section => ({
  //     ...section,
  //     agents: (section.agents || []).map(agent => ({
  //       ...agent,
  //       document_mappings: newDocumentMappings,
  //       par_style: agent.par_style ?? null
  //     }))
  //   }));
  // }
  const allDocumentMappings = getAllFabricDocuments();

    sectionsWithTempIds = sectionsWithTempIds.map(section => ({
      ...section,
      agents: (section.agents || []).map(agent => {
        const agentMappings = agent.tags?.length
          ? getAllFabricDocuments(agent.tags)
          : allDocumentMappings;
        return {
          ...agent,
          document_mappings: agentMappings.length > 0
            ? agentMappings
            : (agent.document_mappings || []),
          par_style: agent.par_style ?? null
        };
      })
    }));

  // 3. Convert to dictionary and update state
  const { sectionsDict, sectionOrder } = convertSectionsToDict(sectionsWithTempIds);

      if (resolvedLangId) {
      setSelectedLanguage({
        id: resolvedLangId,
        language_name:
          resolvedLangName ||
          langsData?.[resolvedLangId] ||
          `Language ${resolvedLangId}`,
      });
    }
  setFormData(prev => ({
    ...prev,
    template_name: prev.template_name.trim() ? prev.template_name : templateName,
    sectionsDict,
    sectionOrder,
    format_json: importedFormat || DEFAULT_REPORT_CONFIG,
    // A doc import carries no frontpage; keep the one the preset import already set.
    frontpage: importedFrontpage || (isDocGenerated ? prev.frontpage : null) || null,
    // The doc-import ("Resubmission Dossier") payload carries no global_prompt key,
    // so fall back to what is already in the form rather than clearing it. `??` not
    // `||`: a preset that genuinely ships an empty prompt still clears it.
    global_prompt: globalPromptUserEditedRef.current && prev.global_prompt?.trim()
      ? prev.global_prompt
      : (importedGlobalPrompt ?? prev.global_prompt ?? ''),
    source_doc_id: importedSourceDocId || null,
    heading_mapping_id: importedHeadingMappingId || null,
    ...(resolvedLangId ? { lang_id: resolvedLangId } : {}),
  }));

  if (onProcessingEnd) {
    onProcessingEnd();
  }
};

const handleTemplateImport = (importedData) => {
    // If there are no existing sections, import directly without confirmation
    const isDocGenerated = !!importedData.doc_generated;
    if (formData.sectionOrder.length === 0) {
      setIsDocGeneratedImport(isDocGenerated);
      // setSourceDocId(isDocGenerated ? (importedData.source_doc_id || null) : null);
      if (importedData.library_template_id) {
        setImportedLibraryTemplateId(importedData.library_template_id);
        setImportedLibraryTemplateName(importedData.name || '');
      }
      performImport(importedData);
    } else {
      // If sections exist, store the incoming template and show the confirmation modal
      setTemplateToImport({ ...importedData, doc_generated: isDocGenerated });
      setShowImportConfirm(true);
    }
  };

  const handleConfirmImport = () => {
    if (templateToImport) {
      const isDocGenerated = !!templateToImport.doc_generated;
      setIsDocGeneratedImport(isDocGenerated);
      // setSourceDocId(isDocGenerated ? (templateToImport.source_doc_id || null) : null);
      if (templateToImport.library_template_id) {
        setImportedLibraryTemplateId(templateToImport.library_template_id);
        setImportedLibraryTemplateName(templateToImport.name || '');
      }
      performImport(templateToImport);
    }
    // Reset state and close the modal
    setShowImportConfirm(false);
    setTemplateToImport(null);
  };
  const handleViewSavedMapping = async () => {
    if (!headingMappingId || isLoadingSavedMapping) return;
    setIsLoadingSavedMapping(true);
    beginProcessing('Loading section mapping');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/doc-import/heading-mapping/get`,
        { fabric_id: formData.fabric_id, heading_mapping_id: headingMappingId },
        { headers }
      );

      if (response.data?.status === 'success') {
        setSavedMapping(response.data);
      } else {
        onError(response.data?.message || 'Failed to load the saved section mapping');
      }
    } catch (error) {
      onError(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load the saved section mapping'
      );
    } finally {
      setIsLoadingSavedMapping(false);
      endProcessing();
    }
  };
 // Add a new S-series sub-section under an existing summary section
 const addSummarySubSection = (parentKey = null) => {
   const newKey = `temp_${Date.now()}_${Math.random()}`;

   // Count existing S-series root sections or children
   let newOrderingId;
   if (!parentKey) {
     const existingSCount = formData.sectionOrder.filter(key => {
       const sec = formData.sectionsDict[key];
       return sec && isSummarySection(sec) && !sec.parent_section_id;
     }).length;
     newOrderingId = `S${existingSCount + 1}`;
   } else {
     const parentSection = formData.sectionsDict[parentKey];
     const parentOid = parentSection?.section_ordering_id || 'S0';
     const childrenCount = formData.sectionOrder.filter(key => {
       const sec = formData.sectionsDict[key];
       return sec && sec.parent_section_id === parentKey;
     }).length;
     newOrderingId = `${parentOid}.${childrenCount + 1}`;
   }

   const insertPosition = findInsertionPosition(parentKey, formData.sectionOrder, formData.sectionsDict);

   const newSection = {
     temp_id: newKey,
     section_name: '',
     parent_section_id: parentKey,
     section_ordering_id: newOrderingId,
     is_summary_section: true,
     agentsDict: {},
     agentOrder: [],
     isNew: true
   };

   setFormData(prev => {
     const newSectionOrder = [...prev.sectionOrder];
     newSectionOrder.splice(insertPosition, 0, newKey);
     return {
       ...prev,
       sectionsDict: { ...prev.sectionsDict, [newKey]: newSection },
       sectionOrder: newSectionOrder
     };
   });
 };

 // Recursively assign S-prefix ordering IDs to a section and all its descendants
 const assignSPrefixRecursive = (parentKey, parentOrderingId, sectionsDict, sectionOrder) => {
   const children = sectionOrder.filter(key =>
     sectionsDict[key]?.parent_section_id === parentKey
   );
   children.forEach((childKey, idx) => {
     const childOrderingId = `${parentOrderingId}.${idx + 1}`;
     sectionsDict[childKey] = {
       ...sectionsDict[childKey],
       is_summary_section: true,
       section_ordering_id: childOrderingId,
       isModified: sectionsDict[childKey].id ? true : undefined
     };
     assignSPrefixRecursive(childKey, childOrderingId, sectionsDict, sectionOrder);
   });
 };

 // Recursively assign ordinary ordering IDs to a section and all its descendants
 const assignOrdinaryPrefixRecursive = (parentKey, parentOrderingId, sectionsDict, sectionOrder) => {
   const children = sectionOrder.filter(key =>
     sectionsDict[key]?.parent_section_id === parentKey
   );
   children.forEach((childKey, idx) => {
     const childOrderingId = `${parentOrderingId}.${idx + 1}`;
     sectionsDict[childKey] = {
       ...sectionsDict[childKey],
       is_summary_section: false,
       section_ordering_id: childOrderingId,
       isModified: sectionsDict[childKey].id ? true : undefined
     };
     assignOrdinaryPrefixRecursive(childKey, childOrderingId, sectionsDict, sectionOrder);
   });
 };

 // Toggle a root section between ordinary and special (S-series)
 const toggleSpecialSection = (sectionKey) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return;

   // Show info popup once per editing session on first conversion to special
   if (!isSummarySection(section) && !specialInfoShownRef.current) {
     specialInfoShownRef.current = true;
     setShowSpecialSectionInfo(true);
   }

   setFormData(prev => {
     const section = prev.sectionsDict[sectionKey];
     if (!section) return prev;

     let updatedSectionsDict = { ...prev.sectionsDict };
     let updatedSectionOrder = [...prev.sectionOrder];
     const descendants = findAllDescendants(sectionKey, updatedSectionsDict, updatedSectionOrder);

     if (!isSummarySection(section)) {
       // ORDINARY → SPECIAL
       // 1. Mark parent as summary with temporary S-prefix
       updatedSectionsDict[sectionKey] = {
         ...section,
         is_summary_section: true,
         section_ordering_id: 'S999',
         isModified: section.id ? true : undefined
       };

       // 2. Recursively mark and assign temp S-prefix ordering for all descendants
       assignSPrefixRecursive(sectionKey, 'S999', updatedSectionsDict, updatedSectionOrder);

       // 3. Move parent + all descendants to the end of S-series block
       const group = [sectionKey, ...descendants];
       updatedSectionOrder = updatedSectionOrder.filter(key => !group.includes(key));
       let insertIdx = 0;
       for (let i = 0; i < updatedSectionOrder.length; i++) {
         const sec = updatedSectionsDict[updatedSectionOrder[i]];
         if (sec && isSummarySection(sec)) {
           insertIdx = i + 1;
         } else {
           break;
         }
       }
       updatedSectionOrder.splice(insertIdx, 0, ...group);

       // 4. Recalculate all S-series numbering — assigns correct S1, S2... and their nested children
       updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
       // 5. Deep-renumber children for each S-root (recalculateSummaryOrderingIds only does 1 level)
       const sRoots = updatedSectionOrder.filter(key => {
         const sec = updatedSectionsDict[key];
         return sec && isSummarySection(sec) && !sec.parent_section_id;
       });
       sRoots.forEach(rootKey => {
         assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, updatedSectionOrder);
       });
       // 6. Recalculate ordinary sections numbering (remaining sections shift up)
       updatedSectionsDict = recalculateSiblingOrderingIds(null, updatedSectionsDict, updatedSectionOrder);

     } else {
       // SPECIAL → ORDINARY
       // 1. Unmark parent with temporary ordering
       updatedSectionsDict[sectionKey] = {
         ...section,
         is_summary_section: false,
         special_section_config: null,
         section_ordering_id: '0',
         isModified: section.id ? true : undefined
       };

       // 2. Recursively unmark and assign temporary ordering to all descendants at any depth
       assignOrdinaryPrefixRecursive(sectionKey, '0', updatedSectionsDict, updatedSectionOrder);

       // 3. Move parent + all descendants to be the first ordinary section (right after S-series)
       const group = [sectionKey, ...descendants];
       updatedSectionOrder = updatedSectionOrder.filter(key => !group.includes(key));
       let insertIdx = 0;
       for (let i = 0; i < updatedSectionOrder.length; i++) {
         const sec = updatedSectionsDict[updatedSectionOrder[i]];
         if (sec && isSummarySection(sec)) {
           insertIdx = i + 1;
         } else {
           break;
         }
       }
       updatedSectionOrder.splice(insertIdx, 0, ...group);

       // 4. Recalculate remaining S-series numbering (S2→S1, S3→S2...)
       updatedSectionsDict = recalculateSummaryOrderingIds(updatedSectionsDict, updatedSectionOrder);
       // 5. Deep-renumber children for each remaining S-root
       const sRoots = updatedSectionOrder.filter(key => {
         const sec = updatedSectionsDict[key];
         return sec && isSummarySection(sec) && !sec.parent_section_id;
       });
       sRoots.forEach(rootKey => {
         assignSPrefixRecursive(rootKey, updatedSectionsDict[rootKey].section_ordering_id, updatedSectionsDict, updatedSectionOrder);
       });
       // 6. Recalculate all ordinary sections numbering (this becomes first, rest shift)
       updatedSectionsDict = recalculateSiblingOrderingIds(null, updatedSectionsDict, updatedSectionOrder);
     }

     return {
       ...prev,
       sectionsDict: updatedSectionsDict,
       sectionOrder: updatedSectionOrder
     };
   });
 };

 // Renders the imported-response toggle+panel for a single agent.
 // Used in both renderSummarySection and renderSection 
 const renderImportedResponse = (agentKey, agent) => {
   if (editingTemplate || !isDocGeneratedImport || !agent.parsed_response) return null;
   return (
     <div className="mt-1">
       <button
         type="button"
         className={`ev-btn-xsmall ev-fs-11 ev-bold-reg ${showResponsePanel[agentKey] ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
         onClick={() => setShowResponsePanel(prev => ({ ...prev, [agentKey]: !prev[agentKey] }))}
       >
         <i className={`bi ${showResponsePanel[agentKey] ? 'bi-eye-slash' : 'bi-eye'} me-1`}></i>
         {showResponsePanel[agentKey] ? 'Hide Imported Response' : 'Show Imported Response'}
       </button>
       {showResponsePanel[agentKey] && (
         <div className="imported-response-panel ev-message-card2 ev-radius-xsmall pb-3 pt-0 mt-1">
           <GenbiResponseComponent
             agent={{
               id: agentKey,
               agent_name: agent.agent_name,
               response: {
                 ...agent.parsed_response,
                 images: (agent.parsed_response?.images || []).map(img => ({
                   ...img,
                   presigned_url: img.preview_url || img.presigned_url || null,
                 })),
               },
               editedResponse: null,
               status: 'COMPLETED',
             }}
             status="COMPLETED"
             isLoading={false}
             fabricId={formData.fabric_id}
             authToken={GetCookie('access_token')}
             compact={true}
             showResponseLanguage={false}
             showResponseSourceBadge={false}
           />
         </div>
       )}
     </div>
   );
 };

 // Render S-series section in locked-down mode (no reorder, no delete, only edit prompts + add)
 const renderSummarySection = (sectionKey, compact = false) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return null;

   const oid = section.section_ordering_id || 'S';
   const level = oid.includes('.') ? oid.split('.').length - 1 : 0;
   const indentPx = level * 15;
   if (compact && level > COMPACT_MAX_SECTION_LEVEL) return null;
   return (
     <div key={sectionKey} className="special-section-glow mb-2">
       <div className="d-flex align-items-center mb-1">
         <div className="d-flex" >
           <span className="ev-message-card3 me-2 ms-1 mt-1 template-section-order-card d-flex ev-radius-xsmall align-items-center ev-fs-11">
             <i className="bi bi-journal-text me-1"></i>{oid}
           </span>
         </div>
         <div style={{ width: '65%' }}>
           <input
             type="text"
             className="formField4 ev-btn-small ev-fs-12 ev-radius-xsmall mt-1"
             placeholder="Section Name"
             value={section.section_name}
             onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
           />
         </div>
         <div className="col-auto ms-2 d-flex align-items-center">
           {section.is_summary_section && (
             <div className="ms-1 ev-message-card2 me-1 template-caret-card">
               {hasSectionManagementAccess && (<>
               <button
                 type="button"
                 className="template-ordering-btn"
                 onClick={() => moveSectionOrder(sectionKey, 'up')}
                 disabled={!canMoveSectionUp(sectionKey)}
               >
                 <i className="bi bi-caret-up-square"></i>
               </button>
               <button
                 type="button"
                 className="template-ordering-btn ms-1"
                 onClick={() => moveSectionOrder(sectionKey, 'down')}
                 disabled={!canMoveSectionDown(sectionKey)}
               >
                 <i className="bi bi-caret-down-square"></i>
               </button>
               </>)}
             </div>
           )}
           {hasSectionManagementAccess && (
           <button
             type="button"
             className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall ev-bold-reg ms-1"
             onClick={() => deleteSection(sectionKey)}
           >
             <i className="bi bi-trash"></i>
           </button>
           )}
           {canSectionManagement && hasSectionManagementAccess && (
             <button
               type="button"
               className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ev-fs-11 ms-1"
               onClick={() => addSummarySubSection(sectionKey)}
             >
               <i className="bi bi-plus-circle-fill"></i> Sub-section
             </button>
           )}
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ev-fs-11 ms-1"
             onClick={() => addAgent(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> Agent
           </button>
           {canSectionManagement && hasSectionManagementAccess && !section.parent_section_id && (
             <button
               type="button"
               className="special-section-toggle-btn active ms-2"
               onClick={() => toggleSpecialSection(sectionKey)}
               title="Remove Special Section"
             >
               <i className="bi bi-star-fill"></i> Special
             </button>
           )}
           {section.is_summary_section && hasSectionManagementAccess && (
             <div
               ref={specialSectionConfigRef}
               className="position-relative d-inline-block ms-2"
             >
               <button
                 type="button"
                 className="no-wrap ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg border-0 d-flex align-items-center"
                 onClick={(e) => {
                   if (openSpecialSectionConfig?.key === sectionKey) {
                     setOpenSpecialSectionConfig(null);
                   } else {
                     const rect = e.currentTarget.getBoundingClientRect();
                     setOpenSpecialSectionConfig({ key: sectionKey, top: rect.bottom + 4, right: document.documentElement.clientWidth - rect.right });
                   }
                 }}
               >
                 Config
                 <i className="bi bi-chevron-down ms-1" />
               </button>
             </div>
           )}
         </div>
       </div>

       {/* Section Config dropdown rendered outside overflow container */}
       {openSpecialSectionConfig?.key === sectionKey && (() => {
         const ssc = section.special_section_config || {};
         return ReactDOM.createPortal(
           <div
             ref={specialSectionConfigRef}
             className="ev-card-4 ev-message-card2 d-flex flex-column gap-1 p-2 rounded-2"
             style={{ position: 'fixed', top: openSpecialSectionConfig.top, right: openSpecialSectionConfig.right, minWidth: '220px', zIndex: 10000 }}
           >
             <span className="ev-small-p-blue ev-bold-med px-2 pt-1 pb-1 text-start text-nowrap">Section Config</span>

             {/* Outside Border */}
             <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-1 rounded-2 border border-secondary border-opacity-25">
               <span className="d-flex align-items-center gap-2 text-nowrap">
                 <i className="bi bi-box-arrow-up-right" />
                 <span>Outside Border</span>
               </span>
               <div
                 className={`agent-lock-toggle agent-lock-toggle--sm flex-shrink-0 ${ssc.is_outside_border ? 'agent-lock-toggle--on' : ''}`}
                 onClick={() => updateSpecialSectionConfig(sectionKey, 'is_outside_border', !ssc.is_outside_border)}
               >
                 <div className="agent-lock-toggle-knob" />
               </div>
             </div>

             {/* Heading BG Color */}
             <div className="ev-card-4 d-flex align-items-center justify-content-between gap-3 px-2 py-0 rounded-2 border border-secondary border-opacity-25" style={{ minHeight: 0, ...(ssc.is_outside_border && { opacity: 0.4, pointerEvents: 'none' }) }}>
               <span className="d-flex align-items-center gap-2 text-nowrap">
                 <i className="bi bi-paint-bucket" />
                 <span>Heading BG</span>
               </span>
               <span className="d-flex align-items-center" style={{ transform: 'scale(0.85)', transformOrigin: 'right center' }}>
               <ColorPickerButton
                 value={ssc.heading_bg_color || '#FFFFFF'}
                 onChange={(color) => updateSpecialSectionConfig(sectionKey, 'heading_bg_color', color)}
                 icon={<i className="bi bi-square-fill" />}
               />
               </span>
             </div>
           </div>,
           document.body
         );
       })()}

       {/* Agents for this summary section */}
       {!compact && section.agentOrder?.map((agentKey) => {
         const agent = section.agentsDict[agentKey];
         if (!agent) return null;

         return (
           <div key={agentKey} className="d-flex align-items-start mt-1 mb-1">
             <div style={{ marginLeft: `${indentPx + 15}px`, width: '65%' }}>
               <div className="ev-card-8 ev-radius-small ev-padding-small">
                 <div className="row mb-1">
                   <div className="col-6">
                     <span className={`agent-tag-popover-anchor ${agent.tags?.length ? '' : 'd-inline-flex'}`}>
                       <span className="ev-message-card3 ev-btn-xsmall ev-fs-11 ms-0 mt-0">
                         <i className="bi bi-robot"></i> S.{oid}. Agent {agent.agent_ordering_id}
                       </span>
                       {agent.tags?.length > 0 && (
                         <div className="agent-tag-popover ev-radius-med">
                           <div className="fw-bold ev-fs-13 mb-1 text-white">
                             <i className="bi bi-tags-fill me-1"></i>Tags
                           </div>
                           <div className="agent-tag-popover-scroll d-flex flex-wrap gap-1">
                             {agent.tags.map((tag, idx) => (
                               <span key={idx} className="agent-tag-pill ev-fs-11">{tag}</span>
                             ))}
                           </div>
                         </div>
                       )}
                     </span>
                   </div>
                   <div className="col-6 text-end">
                     <button
                       type="button"
                       className="ev-btn-blue-fill-round ms-1 ev-bold-reg ev-btn-small text-nowrap"
                       onClick={() => openConfigureModal(sectionKey, agentKey)}
                     >
                       <i className="bi bi-gear-wide"></i> Configure
                     </button>
                      {agent.is_static_text && (
                        <span
                          className="ev-message-card3-green ev-btn-xsmall ev-fs-11 ms-1"
                          title="This agent fills placeholders in authored static text. Editing the text is not available yet."
                        >
                          <i className="bi bi-file-earmark-text"></i> Static
                        </span>
                      )}
                      <div className="position-relative d-inline-block ms-1">
                       <TableStructureSelector
                         sectionKey={sectionKey}
                         agentKey={agentKey}
                         selectedId={agent.table_structure_id}
                         onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                         structures={tableStructures}
                         isStatic={!!agent.is_static_text}
                       />
                     </div> 
                   </div>
                 </div>
                 {/* <input
                   type="text"
                   className="formField4 ev-btn-xsmall ev-fs-12 ev-radius-xsmall mb-2"
                   placeholder="Name"
                   value={agent.agent_name}
                   onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                 /> */}
                 <div className="btn-group custom-btn-group-outer mb-1" role="group">
                   <button
                     type="button"
                     className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                     onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'prompt' }))}
                   >
                     Prompt
                   </button>
                   <button
                     type="button"
                     className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === 'additional_context' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                     onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'additional_context' }))}
                   >
                     Additional Context
                   </button>
                 </div>
                 <div className="mb-0">
                   {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                     <textarea
                       className="formField3 ev-padding-xsmall ev-fs-12"
                       placeholder="Provide context here"
                       value={agent.agent_context || ''}
                       onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                       disabled={!hasSectionManagementAccess}
                     />
                   ) : (
                     <textarea
                       className="formField3 ev-padding-xsmall ev-fs-12"
                       placeholder="Provide additional context here (optional)"
                       value={agent.additional_context || ''}
                       onChange={(e) => updateAgentField(sectionKey, agentKey, 'additional_context', e.target.value)}
                       disabled={!hasSectionManagementAccess}
                     />
                   )}
                 </div>
                 {renderImportedResponse(agentKey, agent)}
               </div>
             </div>
           </div>
         );
       })}

       {/* Render child summary sections recursively */}
       {formData.sectionOrder
         .filter(key => formData.sectionsDict[key]?.parent_section_id === sectionKey)
         .map(childKey => renderSummarySection(childKey, compact))
       }
     </div>
   );
 };

 // Render section with hierarchy
 const renderSection = (sectionKey, compact = false) => {
   const section = formData.sectionsDict[sectionKey];
   if (!section) return null;

   // S-series sections are rendered separately in their own container
   if (isSummarySection(section)) return null;
 
   const level = (section.section_ordering_id?.split('.').length || 1) - 1;
   const indentPx = level * 15;
    if (compact && level > COMPACT_MAX_SECTION_LEVEL) return null;
   // Calculate dynamic widths based on nesting level 
   const baseIndentWidth = 5; // Base width percentage for indent
   const indentWidthPercent = baseIndentWidth + (level * 0.7); // Increase indent width by 2% per level
   const sectionWidthPercent = 70 - (level * 0.7); // Decrease section width by 2% per level
   
   const indentStyle = { 
     marginLeft: `${indentPx}px`, 
     minWidth: `${indentWidthPercent}%`, 
     textAlign: "right" 
   };
 
   return (
     <div key={sectionKey}>
       <div className="d-flex align-items-center justify-content-center mb-1">
         <div className="d-flex" style={{ marginLeft: `${indentPx}px`, minWidth: `${indentWidthPercent}%`, textAlign: "right" }}>
           <span className="ev-message-card2 me-1 ms-2 mt-1 template-section-order-card d-flex align-items-center" >
             {section.section_ordering_id}
           </span>
         </div>
         <div style={{ width: `${sectionWidthPercent}%` }}>
           <input
             type="text"
             className="formField4 ev-btn-small ev-fs-12 ev-radius-xsmall template-viewer-sec-pd-right-2btn mt-1"
             placeholder="Section Name"
             value={section.section_name}
             onChange={(e) => updateSectionField(sectionKey, 'section_name', e.target.value)}
           />
         </div>
         <div className="col-auto d-flex align-items-center">
           {hasSectionManagementAccess && (<>
           <div className="ev-message-card2 me-1 ms-2 template-caret-card">
             <button
               type="button"
               className="template-ordering-btn"
               onClick={() => moveSectionOrder(sectionKey, 'up')}
               disabled={!canMoveSectionUp(sectionKey)}
             >
             <i className="bi bi-caret-up-square"></i>
             </button>
             <button
               type="button"
               className="template-ordering-btn ms-1"
               onClick={() => moveSectionOrder(sectionKey, 'down')}
               disabled={!canMoveSectionDown(sectionKey)}
             >
               <i className="bi bi-caret-down-square"></i>
             </button>
           </div>
           <button
             type="button"
             className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall ev-bold-reg  mt-0 ms-1"
             onClick={() => deleteSection(sectionKey)}
           >
             <i className="bi bi-trash"></i>
           </button>
           </>)}
           {canSectionManagement && hasSectionManagementAccess && (
             <button
               type="button"
               className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ms-3"
               onClick={() => addSection(sectionKey)}
             >
               <i className="bi bi-plus-circle-fill"></i> Section
             </button>
           )}
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg ms-1"
             onClick={() => addAgent(sectionKey)}
           >
             <i className="bi bi-plus-circle-fill"></i> AI Agent
           </button>
           {canSectionManagement && hasSectionManagementAccess && !section.parent_section_id && (
             <button
               type="button"
               className="special-section-toggle-btn ms-2"
               onClick={() => toggleSpecialSection(sectionKey)}
               title="Make Special Section"
             >
               <i className="bi bi-star"></i> Special
             </button>
           )}
         </div>
       </div>

       {/* Render agents for this section */}
       {!compact && section.agentOrder?.map((agentKey) => {
         const agent = section.agentsDict[agentKey];
         if (!agent) return null;
 
         const agentIndex = section.agentOrder.indexOf(agentKey);
         const paragraphStyleOptions = getParagraphStyleOptions();
         const showParagraphStyleButton = paragraphStyleOptions.length > 0; 
         return (
           <div key={agentKey}>
             <div className="d-flex align-items-start justify-content-center mt-1 mb-1">
               <div className="d-flex" style={{ marginLeft: `${indentPx}px`, minWidth: `${indentWidthPercent}%` }}></div>
               <div style={{ width: `${sectionWidthPercent}%` }}>
                 <div className="ev-card-8 ev-radius-small ev-padding-small">
                   <div className ="row mb-1">
                     <div className ="col-6">
                       <span className="agent-tag-popover-anchor">
                         <span className="ev-message-card3 ev-btn-xsmall ev-fs-11 ms-0 mt-0"><i className="bi bi-robot"></i> S.{section.section_ordering_id}. Agent {agent.agent_ordering_id}</span>
                         {agent.tags?.length > 0 && (
                           <div className="agent-tag-popover ev-radius-med">
                             <div className="fw-bold ev-fs-13 mb-1 text-white">
                               <i className="bi bi-tags-fill me-1"></i>Tags
                             </div>
                             <div className="agent-tag-popover-scroll d-flex flex-wrap gap-1">
                               {agent.tags.map((tag, idx) => (
                                 <span key={idx} className="agent-tag-pill ev-fs-11">{tag}</span>
                               ))}
                             </div>
                           </div>
                         )}
                       </span>
                     </div>
                     <div className="col-6 text-end d-flex align-items-center justify-content-end ">{showParagraphStyleButton && (
                       <div
                         ref={openParagraphDropdown === agentKey ? dropdownRef : null}
                         className="position-relative ms-1 d-inline-block"
                       >
                         <button
                           type="button"
                           className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg ev-fs-11"
                           onClick={() => toggleParagraphDropdown(agentKey)}
                         >
                           <i className="bi bi-text-paragraph me-1"></i>
                           Style
                           <i className="bi bi-chevron-down ms-1"></i>
                         </button>
                            {openParagraphDropdown === agentKey && (
                              <div className="position-absolute top-100 end-0 mt-1 ev-card-4 ev-message-card2 d-flex flex-column gap-1 z-1000">
                                {paragraphStyleOptions.map((style) => {
                                  const isSelected = String(agent.par_style ?? "") === String(style.value ?? "");

                                  return (
                                    <button
                                      key={style.value ?? "default"}
                                      type="button"
                                      className={`par-style-item d-flex align-items-center ev-fs-12 ${
                                        isSelected ? "active" : ""
                                      }`}
                                      onClick={() => {
                                        const newValue = isSelected ? null : style.value;
                                        updateAgentField(sectionKey, agentKey, "par_style", newValue);
                                        closeParagraphDropdown();
                                      }}
                                    >
                                      <i
                                        className={`bi ${
                                          isSelected ? "bi-check-circle-fill" : "bi-circle"
                                        } me-2`}
                                      ></i>
                                      {style.label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                       </div>
                     )}
                       <button
                         type="button"
                         className="ev-btn-blue-fill-round ms-1 ev-bold-reg ev-btn-small avatar-tooltip text-nowrap"
                         onClick={() =>
                           openConfigureModal(sectionKey, agentKey)
                         }
                       >
                         <i className="bi bi-gear-wide"></i> Configure
                       </button>
                      {agent.is_static_text && (
                        <span
                          className="ev-message-card3-green ev-btn-xsmall ev-fs-11 ms-1"
                          title="This agent fills placeholders in authored static text. Editing the text is not available yet."
                        >
                          <i className="bi bi-file-earmark-text"></i> Static
                        </span>
                      )}
                      <div className="position-relative d-inline-block ms-1">
                        <TableStructureSelector
                          sectionKey={sectionKey}
                          agentKey={agentKey}
                          selectedId={agent.table_structure_id}
                          onSelect={(id) => updateAgentField(sectionKey, agentKey, 'table_structure_id', id)}
                          structures={tableStructures}
                          isStatic={!!agent.is_static_text}
                        />
                      </div> 
                      </div>
                   </div>
                   {/* <input
                     type="text"
                     className="formField4 ev-btn-xsmall ev-fs-12 ev-radius-xsmall mb-2"
                     placeholder="Name"
                     value={agent.agent_name}
                     onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_name', e.target.value)}
                   /> */}
                   <div className="d-flex align-items-center gap-1 mb-1">
                     <div className="btn-group custom-btn-group-outer" role="group">
                       <button
                         type="button"
                         className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                         onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'prompt' }))}
                       >
                         Prompt
                       </button>
                       <button
                         type="button"
                         className={`btn genbi-custom-btn-group-inner prompt-toggle-sm ${activePromptTab[agentKey] === 'additional_context' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                         onClick={() => setActivePromptTab(prev => ({ ...prev, [agentKey]: 'additional_context' }))}
                       >
                         Additional Context
                       </button>
                     </div>
                   </div>
                   <div className="mb-0">
                     {(!activePromptTab[agentKey] || activePromptTab[agentKey] === 'prompt') ? (
                       <textarea
                         className="formField3 ev-padding-xsmall ev-fs-12"
                         placeholder="Provide context here"
                         value={agent.agent_context || ''}
                         onChange={(e) => updateAgentField(sectionKey, agentKey, 'agent_context', e.target.value)}
                         disabled={!hasSectionManagementAccess}
                       />
                     ) : (
                       <textarea
                         className="formField3 ev-padding-xsmall ev-fs-12"
                         placeholder="Provide additional context here (optional)"
                         value={agent.additional_context || ''}
                         onChange={(e) => updateAgentField(sectionKey, agentKey, 'additional_context', e.target.value)}
                         disabled={!hasSectionManagementAccess}
                       />
                     )}
                   </div>

                    {renderImportedResponse(agentKey, agent)}
                 </div>
               </div>
 
               <div className="col-4">
                 {hasSectionManagementAccess && (<>
                 <div className="ev-message-card2 me-1 ms-2  template-caret-card" >
                   <button
                     type="button"
                     className="template-ordering-btn"
                     onClick={() => moveAgentOrder(sectionKey, agentKey, 'up')}
                     disabled={agentIndex === 0}
                   >
                   <i className="bi bi-caret-up-square"></i>
                   </button>
                   <button
                     type="button"
                     className="template-ordering-btn ms-1"
                     onClick={() => moveAgentOrder(sectionKey, agentKey, 'down')}
                     disabled={agentIndex === section.agentOrder.length - 1}
                   >
                     <i className="bi bi-caret-down-square"></i>
                   </button>
                 </div>
                 
                 <button
                   type="button"
                   className="ev-btn-red-fill-round ev-icon-btn-padding-small ev-radius-xsmall  ms-1"
                   onClick={() => deleteAgent(sectionKey, agentKey)}
                 >
                   <i className="bi bi-trash"></i>
                 </button>
                 </>)}
               </div>
             </div>
           </div>
         );
       })}
     </div>
   );
 };


const getImportConfirmBodyContent = () => {
    return (
      <div>
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> You have existing sections and agents in your preset.
        </div>
        <p>Importing a new Preset will <strong>replace all</strong> of your current work.</p>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

return (
<div className="ev-card-2 w-100 ev-padding-med dashboard-form w-100 mt-2">
 {/* Header with Create Template and Controls */}
<div className="row mb-1 align-items-center">
  <div className="col-auto">
    <p className="ev-med-p-blue mb-1">
      {editingTemplate ? 'Edit Preset' : 'Import Preset'}
    </p>
  </div>
  <div className="col-auto ms-auto mb-2 d-flex align-items-center gap-2">
    {canReportFormatting && hasSectionManagementAccess && (
    <button
      className="ev-btn-blue-outline-round ev-btn-small"
      onClick={openReportFormatting}
    >
      <i className="bi bi-file-earmark-text-fill"></i> Report Formatting
    </button>
    )}
    {/* <div className="ev-message-card4 ev-btn-small d-flex align-items-center pe-1">
        <div className="d-flex align-items-center me-2">
          <div className="form-switch">
              <input
                className="form-check-input custom-switch"
                type="checkbox"
                id="valueMessageToggle"
                checked={useValueMessage}
                onChange={handleValueMessageToggle}
              />
            </div>
            <span className="ms-2">
              Include Value Messages
            </span>
            <button
              type="button"
              className={`ev-btn-blue-fill-round ms-2 ev-bold-reg ev-btn-small avatar-tooltip text-nowrap`}
              onClick={openValueMessageModal}
              disabled={!useValueMessage || !formData.fabric_id}
              data-tooltip={
                !useValueMessage
                  ? 'Please check "Include Value Messages" first to configure'
                  : !formData.fabric_id
                    ? 'Please select a project'
                    : undefined
              }
            >
              <i className="bi bi-gear-wide"></i> Configure
            </button>
        </div>
    </div> */}
    <div className="ev-card-8 ev-radius-large ev-padding-xsmall">
      <LanguageSelector
        options={langsData}
        isLoading={isLoadingLangs}
        selectedLanguage={selectedLanguage}
        onLanguageSelect={(langObj) => handleTranslateSelect(langObj.id, langObj.language_name)}
      />
    </div>
  </div>
</div>
 
       <form onSubmit={handleSubmit}>
         <div className="row mb-2">
           <div className="col-6">
             <div className="form-group mb-1">
               <label className="ev-reg-p-dark mb-1">Dossier Name <span className="text-danger">*</span></label>
               <input
                 type="text"
                 className="formField3"
                 value={formData.template_name}
                 onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                 required
                 placeholder="Enter Dossier Name"
                 disabled={isLoading || !canWriteTemplate}
               />
             </div>
           </div>
 
           <div className="col-6">
             <div className="form-group mb-1">
               <label className="ev-reg-p-dark mb-1">Project Name </label>
               <FabricSelector
                  selectedFabricId={formData.fabric_id}
                  selectedFabricName={formData.fabric_name}
                  onFabricSelect={handleFabricSelection}
                  // Locked on edit, and on create too — the project is fixed by
                  // the card the user clicked to get here.
                  disabled={!!editingTemplate || !!initialFabricId}
                  fabricselectorapi={editingTemplate ? null : `${API_BASE_URL}/fabric_unstructured_data_tree`} // Add this line
                  closeOnOutsideClick={true}
               />
             </div>
           </div>
         </div>
          <div className="mb-2">
           {/* Global Prompt */}
           <label className="ev-reg-p-dark mb-1">
             Global Prompt <span className="ev-small-p-blue"><i>(Optional — prepended to every agent's context)</i></span>
           </label>
           <textarea
             className="formField3 ev-fs-12 px-3 py-2"
             rows={2}
             placeholder="e.g., Focus only on RCT evidence, use APA citation style, write in formal academic tone..."
             value={formData.global_prompt}
             onChange={(e) => { globalPromptUserEditedRef.current = true; setFormData(prev => ({ ...prev, global_prompt: e.target.value })); }}
             disabled={!canWriteTemplate}
           />
         </div>
         <div className="mb-2">
 
            {/* Sections Management */}
               {formData.sectionOrder.length === 0 && (
                 <div className="ev-alert-card-warning mt-2 mb-2">
                   <p className="mb-0">
                     <i className="bi bi-info-circle me-1"></i>
                     &nbsp;
                      {formData.fabric_id ? 'Import a preset to generate sections and agents automatically' : 'Select a project'}
                   </p>
                 </div>
               )}
 
             <div className="ev-card-3 vh-40">
              <div className="d-flex align-items-center gap-2 flex-wrap">
              {canSectionManagement && hasSectionManagementAccess && (
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-bold-reg avatar-tooltip"
                  onClick={() => addSection()}
                  data-tooltip={!formData.fabric_id ? "Please select a project" : undefined}
                  disabled={!formData.fabric_id}
                >
                  <i className="bi bi-plus-circle"></i> Super Section
                </button>
              )}
              {!editingTemplate && can(PERMISSIONS.TEMPLATE_CREATE) && (
                <div className="position-relative" style={{ flexShrink: 0 }}>
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg avatar-tooltip"
                    onClick={() => setIsTemplateLibraryOpen(!isTemplateLibraryOpen)}
                    // data-tooltip={!formData.fabric_id ? "Please select a fabric" : "Import a pre-defined template"}
                    // disabled={!formData.fabric_id}
                  >
                    <i className="bi bi-box-arrow-in-down"></i> Select Preset
                  </button>

                  <GenbiTemplateLibraryList
                    isOpen={isTemplateLibraryOpen}
                    onClose={() => setIsTemplateLibraryOpen(false)}
                    onImport={handleTemplateImport}
                    onError={onError}
                    fabricId={formData.fabric_id}
                    templateName={formData.template_name}
                    onProcessingStart={beginProcessing}
                    onProcessingEnd={endProcessing}
                    onTemplateCreated={onTemplateCreated}
                  />
                </div>
              )}

              {!editingTemplate && importedLibraryTemplateId && canWriteTemplate && canUseResubmission && (
                <div className="position-relative" style={{ flexShrink: 0 }}>
                  <button
                    type="button"
                    className="ev-btn-gradient-fill-round ev-bold-reg avatar-tooltip"
                    data-tooltip="Auto-build sections & agents from an existing document"
                    onClick={() => setIsImportFromDocOpen(!isImportFromDocOpen)}
                  >
                    <i className="bi bi-magic"></i> Resubmission Dossier
                  </button>

                  <ImportFromDocumentModal
                    isOpen={isImportFromDocOpen}
                    onClose={() => setIsImportFromDocOpen(false)}
                    onImport={handleTemplateImport}
                    onError={onError}
                    fabricId={formData.fabric_id}
                    templateName={formData.template_name}
                    libraryTemplateId={importedLibraryTemplateId}
                    libraryTemplateName={importedLibraryTemplateName}
                    onProcessingStart={beginProcessing}
                    onProcessingEnd={endProcessing}
                    onFullScreenChange={handleImportFullScreenChange}
                  />
                </div>
              )}
              {editingTemplate && headingMappingId && (
                <>
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-bold-reg avatar-tooltip ms-2"
                    data-tooltip="View the document-to-Preset section mapping this Preset was built from"
                    disabled={isLoadingSavedMapping}
                    onClick={handleViewSavedMapping}
                  >
                    <i className="bi bi-diagram-3"></i> View Section Mapping
                  </button>

                  <GenbiHeadingMappingModal
                    isOpen={!!savedMapping}
                    mappingData={savedMapping}
                    docName={savedMapping?.doc_name || ''}
                    libraryTemplateName={savedMapping?.library_template_name || ''}
                    onCancel={() => setSavedMapping(null)}
                    readOnly
                  />
                </>
              )}
            </div>

                 {/* All sections in unified scroll — special (with glow) first, then ordinary */}
                 <div className={`scrollable-element mt-2 ${formData.sectionOrder.length === 0 ? "vh-25" : "vh-30"}`}>
                   {formData.sectionOrder
                     .filter(key => {
                       const sec = formData.sectionsDict[key];
                       return sec && isSummarySection(sec) && !sec.parent_section_id;
                     })
                     .map(key => renderSummarySection(key, isSectionTreeCompact))
                   }
                   {formData.sectionOrder
                     .filter(key => {
                       const sec = formData.sectionsDict[key];
                       return sec && !isSummarySection(sec);
                     })
                     .map((sectionKey) => renderSection(sectionKey, isSectionTreeCompact))
                   }
                 </div>
             </div>
         </div>
         
         <div className="mt-0 text-end" >
           {/* The form's own save. Editing answers to this preset's update
               right; creating answers to the create route, since there is no
               preset yet to be specific about. */}
           {!canWriteTemplate && (
             <span className="ev-small-p-blue me-2">
               <i className="bi bi-eye me-1"></i> View only
             </span>
           )}
           {canWriteTemplate && (
             <button
               type="submit"
               className="ev-btn-blue-fill-round ev-bold-reg"
               disabled={isLoading || !formData.fabric_id}
             >
               <i className="bi bi-cursor-fill"></i> {editingTemplate ? 'Update Preset' : 'Create Preset'}
             </button>
           )}
           <button
             type="button"
             className="ev-btn-blue-outline-round ev-bold-reg ms-2"
             onClick={onCancel}
           >
             {canWriteTemplate ? 'Cancel' : 'Close'}
           </button>
         </div>
       </form>
         {/* Value Message Configuration Modal */}
         {valueMessageModalOpen && (
         <ConfigureVM
           initialConfig={vmConfiguration}
           fabricTreeData={fabricTreeData}
           onSave={handleSaveVmConfiguration}
           onClose={closeValueMessageModal}
         />
       )}
 
       {/* Document Selector Modal */}
       {/* {documentSelectorOpen && (
         <DocumentSelector
           isOpen={documentSelectorOpen}
           fabricTreeData={fabricTreeData}
           selectedDocIds={
             currentAgentIndex.sectionKey && currentAgentIndex.agentKey
               ? formData.sectionsDict[currentAgentIndex.sectionKey]?.agentsDict[currentAgentIndex.agentKey]?.document_mappings
                   ?.filter(d => d.selected)
                   ?.map(d => d.doc_id) || []
               : []
           }
           onDocumentSelect={handleDocumentSelection}
           onClose={() => setDocumentSelectorOpen(false)}
           sectionName={formData.sectionsDict[currentAgentIndex.sectionKey]?.section_name}
           sectionOrderingId={formData.sectionsDict[currentAgentIndex.sectionKey]?.section_ordering_id}
           agentName={formData.sectionsDict[currentAgentIndex.sectionKey]?.agentsDict[currentAgentIndex.agentKey]?.agent_name}
           agentOrderingId={formData.sectionsDict[currentAgentIndex.sectionKey]?.agentsDict[currentAgentIndex.agentKey]?.agent_ordering_id}
         />
       )} */}
 

        <ConfirmationModal
        show={showImportConfirm}
        onClose={() => setShowImportConfirm(false)}
        onConfirm={handleConfirmImport}
        title="Confirm Preset Import"
        message="" // Message is handled by bodyContent
        confirmButtonText="Import & Replace"
        confirmButtonClass="btn-warning"
        bodyContent={getImportConfirmBodyContent()}
      />

      {agentConfigureOpen && (
      <AgentConfigure
        isOpen={agentConfigureOpen}
        onClose={closeAgentConfigure}
        formData={formData}
        sectionKey={agentToConfigure.sectionKey}
        agentKey={agentToConfigure.agentKey}
        fabricTreeData={fabricTreeData}
        updateAgentField={updateAgentField}
      />
    )}
    {showReportFormatting && (
    <ReportFormattingModal
      onClose={closeReportFormatting}
      onSave={handleReportFormattingSave}
      templateId={editingTemplate?.id}
      initialConfig={formData.format_json}
      canSave={canWriteTemplate}
    />
  )}

      {/* Preset refer_agents that could not be applied on import */}
      <ConfirmationModal
        show={importReferWarnings.length > 0}
        onClose={() => setImportReferWarnings([])}
        onConfirm={() => setImportReferWarnings([])}
        title="Refer Agents: Please Review"
        confirmButtonText="OK"
        confirmButtonClass="btn-primary"
        showCancel={false}
        bodyContent={
          <div>
            <p className="mb-2">The preset was imported. Review these refer-agent links in Configure → Refer Agents (circular links are kept as-is):</p>
            <ul className="mb-0 ps-3" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
              {importReferWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        }
      />

      {/* Special Section Info — shown only on first-ever conversion */}
      <ConfirmationModal
        show={showSpecialSectionInfo}
        onClose={() => setShowSpecialSectionInfo(false)}
        onConfirm={() => setShowSpecialSectionInfo(false)}
        title="Special Sections"
        confirmButtonText="Got it"
        confirmButtonClass="btn-primary"
        showCancel={false}
        bodyContent={
          <div className="text-center">
            <div className="special-info-container mb-3">
              <div className="special-info-doc-wrapper">
                {/* Before state */}
                <div className="special-info-state">
                  <span className="special-info-state-label">Before</span>
                  <div className="special-info-doc">
                    <div className="special-info-item special-info-item-toc">
                      <i className="bi bi-list-ul me-1"></i>Table of Contents
                    </div>
                    <div className="special-info-item special-info-item-sec">1. Section A</div>
                    <div className="special-info-item special-info-item-target">
                      2. Section B
                    </div>
                    <div className="special-info-item special-info-item-sec">3. Section C</div>
                  </div>
                </div>

                {/* Arrow */}
                <div className="special-info-arrow-center">
                  <i className="bi bi-arrow-right-short"></i>
                </div>

                {/* After state */}
                <div className="special-info-state">
                  <span className="special-info-state-label">After</span>
                  <div className="special-info-doc">
                    <div className="special-info-item special-info-item-toc">
                      <i className="bi bi-list-ul me-1"></i>Table of Contents
                    </div>
                    <div className="special-info-item special-info-item-special special-info-item-glow">
                      <i className="bi bi-star-fill me-1"></i>S1. Section B
                    </div>
                    <div className="special-info-item special-info-item-sec">1. Section A</div>
                    <div className="special-info-item special-info-item-sec">2. Section C</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="ev-fs-13 mb-2">
              <strong>Special sections are priority sections</strong> that render right after the Table of Contents — <em>ahead of</em> all regular sections.
            </p>
            <p className="ev-fs-12 text-muted mb-0">
              Toggle any root section to make it special. It moves to the top automatically with all its subsections and agents. Toggle again to move it back.
            </p>
          </div>
        }
      />

    </div>
   );
 });

 GenbiTemplateForm.displayName = 'GenbiTemplateForm';
  
 export default GenbiTemplateForm;